/**
 * Wire types mirroring `docs/10-runtime-architecture.md §5` and the
 * payload contracts in `supabase/functions/<name>/index.ts`.
 *
 * These types are runtime-discovered from the wire, not imported from
 * `@kmi/contracts` (which holds the locked enum/branded-id surface). The
 * frontend never writes business state, so type drift here is bounded to
 * read-only display.
 */

export type WireErrorCode =
  | 'unauthenticated'
  | 'permission_denied'
  | 'entitlement_denied'
  | 'validation_error'
  | 'not_found'
  | 'conflict'
  | 'rate_limited'
  // Task 298 (295A §6.1.1) — commercial standing is a SEPARATE AXIS from
  // permission and from entitlement. The UI must distinguish "not
  // activated" (route to the activation screen) from "not permitted"
  // (render the denied state), and `permission_denied` cannot carry the
  // detail that would let it. Mirrors the edge union in
  // `supabase/functions/_shared/errors.ts`.
  | 'commercial_activation_required'
  | 'internal_error';

export interface SuccessEnvelope<T> {
  readonly ok: true;
  readonly data: T;
  readonly meta: { readonly request_id: string; readonly idempotency_replay?: boolean };
}

export interface ErrorBody {
  readonly code: WireErrorCode;
  readonly request_id?: string;
  readonly fields?: Readonly<Record<string, ReadonlyArray<string>>>;
  readonly feature_key?: string;
  readonly detail?: string;
  // Task 298 — present on `commercial_activation_required` ONLY, and only
  // for agency-category actors. A client-category actor of a
  // non-operational agency receives the bare code and learns nothing
  // about the agency’s commercial standing (295A §5.4).
  readonly commercial_state?: string;
}

export interface ErrorEnvelope {
  readonly ok: false;
  readonly error: ErrorBody;
}

// ── me-context ─────────────────────────────────────────────────────────

export type RoleCategory = 'platform' | 'agency' | 'client' | 'influencer';

export type ClientStatus = 'onboarding' | 'active' | 'paused' | 'archived';

export interface ActorSummary {
  readonly user_id: string;
  readonly profile_id: string | null;
}

export interface ProfileSummary {
  readonly id: string;
  readonly auth_user_id: string;
  readonly email: string;
  readonly full_name: string | null;
  readonly avatar_url: string | null;
  readonly is_active: boolean;
  readonly created_at: string;
}

export interface OrganizationSummary {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly primary_locale: string;
  readonly timezone: string;
}

export interface RoleSummary {
  readonly key: string;
  readonly category: RoleCategory;
  readonly organization_id: string | null;
}

export interface RoleRow {
  readonly id: string;
  readonly key: string;
  readonly category: Extract<RoleCategory, 'agency' | 'client'>;
  readonly organization_id: string | null;
  readonly name_en: string | null;
  readonly name_ar: string | null;
  readonly description: string | null;
  readonly is_system: boolean;
  readonly is_active: boolean;
}

export interface ListRolesData {
  readonly roles: ReadonlyArray<RoleRow>;
}

export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

export interface InvitationClientSummary {
  readonly id: string;
  readonly name: string;
  readonly status: ClientStatus;
}

export interface UserInvitationRow {
  readonly id: string;
  readonly email: string;
  readonly client_id: string | null;
  readonly role_id: string;
  readonly status: InvitationStatus;
  readonly expires_at: string;
  readonly accepted_at: string | null;
  readonly accepted_user_id: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  /**
   * Task 112 (TEAM-1B) — count of CAT memberships the invitee will be
   * granted at accept time. `0` for plain agency invites and for every
   * client-category invite. Surfaced for the pending-list chip.
   */
  readonly intended_client_assignments_count: number;
  readonly role: Pick<RoleRow, 'id' | 'key' | 'category' | 'name_en' | 'name_ar'> | null;
  readonly client: InvitationClientSummary | null;
}

export interface ListUserInvitationsData {
  readonly invitations: ReadonlyArray<UserInvitationRow>;
}

export interface UserRoleProfileSummary {
  readonly auth_user_id: string;
  readonly email: string;
  readonly full_name: string | null;
  readonly avatar_url: string | null;
  readonly is_active: boolean;
}

export interface UserRoleClientAssignmentSummary {
  readonly id: string;
  readonly client_id: string;
  readonly created_at: string;
  readonly client: InvitationClientSummary | null;
}

export interface UserRoleRow {
  readonly user_role_id: string;
  readonly user_id: string;
  readonly role_id: string;
  readonly started_at: string;
  readonly created_at: string;
  readonly role: Pick<RoleRow, 'id' | 'key' | 'category' | 'name_en' | 'name_ar'> | null;
  readonly profile: UserRoleProfileSummary | null;
  readonly client_assignments: ReadonlyArray<UserRoleClientAssignmentSummary>;
}

export interface ListUserRolesData {
  readonly user_roles: ReadonlyArray<UserRoleRow>;
}

export interface SendUserInvitationInput {
  readonly email: string;
  readonly role_id: string;
  readonly client_id?: string | null;
  /**
   * Task 112 (TEAM-1B) — agency-category invitations only. Optional set
   * of clients the invitee joins as a CAT member at accept time. Cap 50.
   * Omit (or `undefined`) for "no CAT bootstrap." Rejected by the EF
   * when the resolved role's category is not `agency`.
   */
  readonly client_ids?: ReadonlyArray<string>;
}

// Task 112D — `email_delivery` reports whether the backend successfully
// dispatched the invitation email via Resend. The invitation row is
// always created on success; this field tells the FE whether to render
// the success banner ('sent') or the copy-link fallback ('not_configured'
// when the Resend env vars are absent, 'failed' when the Resend API
// call itself errored).
export type InvitationEmailDelivery = 'sent' | 'not_configured' | 'failed';

export interface SendUserInvitationData {
  readonly invitation_id: string;
  readonly raw_token: string;
  readonly expires_at: string;
  readonly client_id: string | null;
  readonly email_delivery: InvitationEmailDelivery;
}

export interface RevokeUserInvitationInput {
  readonly invitation_id: string;
}

export interface RevokeUserInvitationData {
  readonly invitation_id: string;
  readonly status: 'revoked';
  readonly client_id: string | null;
}

export interface ClientAssignmentSummary {
  readonly client_id: string;
  readonly client_name: string;
  readonly client_status: ClientStatus;
  readonly started_at: string;
}

export interface SupervisedSubject {
  readonly user_id: string;
  readonly profile_id: string | null;
  readonly full_name: string | null;
  readonly email: string | null;
}

export interface SupervisedSession {
  readonly id: string;
  readonly organization_id: string;
  readonly mode: 'view_as' | 'act_as';
  readonly reason: string | null;
  readonly started_at: string;
  readonly subject: SupervisedSubject;
}

export interface MeContext {
  readonly actor: ActorSummary;
  readonly profile: ProfileSummary | null;
  readonly organization: OrganizationSummary | null;
  readonly system_admin: boolean;
  readonly roles: ReadonlyArray<RoleSummary>;
  readonly permissions: ReadonlyArray<string>;
  readonly client_assignments: ReadonlyArray<ClientAssignmentSummary>;
  readonly entitlements: Readonly<Record<string, boolean>>;
  readonly supervised_session: SupervisedSession | null;
  readonly locale: string;
  readonly timezone: string;
}

// ── list-clients ──────────────────────────────────────────────────────

export interface ClientRow {
  readonly id: string;
  readonly name: string;
  readonly status: ClientStatus;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListClientsData {
  readonly clients: ReadonlyArray<ClientRow>;
}

// ── create-client ─────────────────────────────────────────────────────

export interface CreateClientInput {
  readonly name: string;
  readonly status?: ClientStatus;
  readonly notes?: string | null;
}

export interface CreateClientData {
  readonly client: ClientRow;
}

// ── get-client-overview ────────────────────────────────────────────────

export interface BrandInfo {
  readonly id: string;
  readonly brand_name: string;
  readonly industry: string | null;
  readonly website_url: string | null;
  readonly primary_locale: string | null;
}

export interface TargetAudience {
  readonly id: string;
  readonly label: string;
  readonly is_primary: boolean;
}

export interface ClientGoal {
  readonly id: string;
  readonly goal: string;
  readonly metric_name: string | null;
  readonly target_value: string | null;
}

export interface ClientOverviewData {
  readonly client: ClientRow;
  readonly brand_info: BrandInfo | null;
  readonly target_audiences: ReadonlyArray<TargetAudience>;
  readonly goals: ReadonlyArray<ClientGoal>;
}

// ── client team (Task 109) ────────────────────────────────────────────
// The "client team" is the set of active `client_assignments` rows for a
// client. A member's `disciplines` are derived server-side from their
// global agency role(s); the FE renders them, it does not compute them.

export interface ClientTeamMemberRole {
  readonly id: string;
  readonly key: string;
  readonly category: RoleCategory;
  readonly name_en: string | null;
  readonly name_ar: string | null;
}

export interface ClientTeamMemberProfile {
  readonly auth_user_id: string;
  readonly email: string;
  readonly full_name: string | null;
  readonly avatar_url: string | null;
  readonly is_active: boolean;
}

export interface ClientTeamMember {
  readonly assignment_id: string;
  readonly user_id: string;
  readonly assigned_at: string;
  readonly assigned_by_user_id: string | null;
  readonly profile: ClientTeamMemberProfile | null;
  readonly roles: ReadonlyArray<ClientTeamMemberRole>;
  readonly disciplines: ReadonlyArray<ContentDiscipline>;
}

export interface ListClientTeamData {
  readonly client_id: string;
  readonly members: ReadonlyArray<ClientTeamMember>;
}

export interface AssignClientTeamMemberInput {
  readonly client_id: string;
  readonly target_user_id: string;
  readonly reason?: string | null;
}

export interface AssignClientTeamMemberData {
  readonly assignment_id: string;
  readonly client_id: string;
  readonly user_id: string;
  readonly assigned_at: string;
}

export interface EndClientTeamAssignmentInput {
  readonly assignment_id: string;
  readonly reason?: string | null;
}

export interface EndClientTeamAssignmentData {
  readonly assignment_id: string;
  readonly client_id: string;
  readonly user_id: string;
  readonly ended_at: string;
  readonly already_ended: boolean;
}

// ── Task 122A — client-scoped permission overrides ─────────────────
// Mirrors `packages/contracts/src/client-team-permissions.ts` (and the
// Deno mirror in `supabase/functions/_shared/contracts/`). FE-side type
// duplication matches the existing pattern in this file (see
// `ClientTeamMember` etc.) — `types.ts` does not import from
// `@kmi/contracts`; every shape is defined locally so the web app
// keeps a tight bundle and the contracts package stays an EF-side
// authority. The Task 122A shape test pins the FE↔contract parity.

export type ClientTeamPermissionEffect = 'allowed' | 'denied';

export interface ClientTeamPermissionOverride {
  readonly id: string;
  readonly client_id: string;
  readonly user_id: string;
  readonly permission_id: string;
  readonly permission_key: string;
  readonly effect: ClientTeamPermissionEffect;
  readonly reason: string;
  readonly granted_by_user_id: string;
  readonly granted_at: string;
}

export interface ListClientTeamPermissionsData {
  readonly client_id: string;
  readonly user_id: string | null;
  readonly overrides: ReadonlyArray<ClientTeamPermissionOverride>;
}

export interface GrantClientTeamPermissionInput {
  readonly client_id: string;
  readonly target_user_id: string;
  readonly permission_key: string;
  readonly effect: ClientTeamPermissionEffect;
  readonly reason: string;
}

export interface GrantClientTeamPermissionData {
  readonly override_id: string;
  readonly client_id: string;
  readonly target_user_id: string;
  readonly permission_key: string;
  readonly effect: ClientTeamPermissionEffect;
  readonly granted_at: string;
}

export interface RevokeClientTeamPermissionInput {
  readonly override_id: string;
  readonly reason?: string | null;
}

export interface RevokeClientTeamPermissionData {
  readonly override_id: string;
  readonly client_id: string;
  readonly target_user_id: string;
  readonly permission_key: string;
  readonly prior_effect: ClientTeamPermissionEffect;
  readonly revoked_at: string;
  readonly already_revoked: boolean;
}

// Task 133 hotfix — see contracts/client-team-permissions.ts for the
// shape change rationale. `public.permissions` carries `description`,
// not `name_en` / `name_ar`; the picker reads description with key
// fallback.
export interface PermissionCatalogRow {
  readonly id: string;
  readonly key: string;
  readonly namespace: string;
  readonly description: string | null;
}

export interface ListPermissionsData {
  readonly permissions: ReadonlyArray<PermissionCatalogRow>;
}

// ── update-client ─────────────────────────────────────────────────────

export interface UpdateClientInput {
  readonly client_id: string;
  readonly name?: string;
  readonly status?: ClientStatus;
  readonly notes?: string | null;
}

export interface UpdateClientData {
  readonly client: ClientRow;
}

// ── upsert-client-brand-info ──────────────────────────────────────────

export interface UpsertClientBrandInfoInput {
  readonly client_id: string;
  readonly brand_name: string;
  readonly industry?: string | null;
  readonly website_url?: string | null;
  readonly primary_locale?: string | null;
  readonly notes?: string | null;
}

export interface UpsertClientBrandInfoData {
  readonly brand_info: BrandInfo;
}

// ── create-client-target-audience ─────────────────────────────────────

export interface CreateClientTargetAudienceInput {
  readonly client_id: string;
  readonly label: string;
  readonly description?: string | null;
  readonly is_primary?: boolean;
}

export interface CreateClientTargetAudienceData {
  readonly audience: TargetAudience;
}

// ── create-client-goal ────────────────────────────────────────────────

export interface CreateClientGoalInput {
  readonly client_id: string;
  readonly goal: string;
  readonly metric_name?: string | null;
  readonly target_value?: string | null;
}

export interface CreateClientGoalData {
  readonly goal: ClientGoal;
}

// ── list-client-initiatives + create-client-initiative ────────────────

export type InitiativeStatus = 'planning' | 'in_progress' | 'at_risk' | 'completed' | 'cancelled';

export interface InitiativeListRow {
  readonly id: string;
  readonly name: string;
  readonly status: InitiativeStatus;
  readonly start_date: string | null;
  readonly target_end_date: string | null;
  readonly completed_at: string | null;
  readonly cancelled_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListClientInitiativesData {
  readonly initiatives: ReadonlyArray<InitiativeListRow>;
}

export interface CreateClientInitiativeInput {
  readonly client_id: string;
  readonly name: string;
  readonly description?: string | null;
  readonly start_date?: string | null;
  readonly target_end_date?: string | null;
}

export interface CreateClientInitiativeData {
  readonly initiative: InitiativeListRow;
}

// ── client onboarding profile (Task 045A) ─────────────────────────────

/**
 * Long-form brand / sales / market context captured by the onboarding
 * wizard (Task 045B). Wired to `get-client-onboarding-profile` and
 * `upsert-client-onboarding-profile`. The audit columns
 * `created_by_user_id` / `updated_by_user_id` are deliberately NOT on
 * the wire.
 */
export interface OnboardingProductServiceItem {
  readonly name: string;
  readonly description?: string;
  readonly price_range?: string;
}

export interface OnboardingCompetitorItem {
  readonly name: string;
  readonly website_url?: string;
  readonly notes?: string;
}

export interface ClientOnboardingProfile {
  readonly id: string;
  readonly client_id: string;
  readonly arabic_description: string | null;
  readonly english_description: string | null;
  readonly vision: string | null;
  readonly mission: string | null;
  readonly values_text: string | null;
  readonly usp: string | null;
  readonly strengths: string | null;
  readonly weaknesses: string | null;
  readonly offers_notes: string | null;
  readonly products_services: ReadonlyArray<OnboardingProductServiceItem>;
  readonly competitors: ReadonlyArray<OnboardingCompetitorItem>;
  readonly internal_notes: string | null;
  readonly progress: Readonly<Record<string, string | number | boolean | null>>;
  readonly completed_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface GetClientOnboardingProfileData {
  readonly onboarding_profile: ClientOnboardingProfile | null;
}

/**
 * PATCH-style input for the upsert endpoint. Each editable field is
 * optional. Absent keys preserve the existing value; explicit `null`
 * clears the field. `values_text` is the canonical column name; the
 * runtime also accepts `values` as an alias.
 */
export interface UpsertClientOnboardingProfileInput {
  readonly client_id: string;
  readonly arabic_description?: string | null;
  readonly english_description?: string | null;
  readonly vision?: string | null;
  readonly mission?: string | null;
  readonly values_text?: string | null;
  readonly usp?: string | null;
  readonly strengths?: string | null;
  readonly weaknesses?: string | null;
  readonly offers_notes?: string | null;
  readonly internal_notes?: string | null;
  readonly products_services?: ReadonlyArray<OnboardingProductServiceItem>;
  readonly competitors?: ReadonlyArray<OnboardingCompetitorItem>;
  readonly progress?: Readonly<Record<string, string | number | boolean | null>>;
  readonly completed_at?: string | null;
}

export interface UpsertClientOnboardingProfileData {
  readonly onboarding_profile: ClientOnboardingProfile;
}

// ── agency services / packages / assignments (Task 046) ──────────────

export type SystemCapabilityKey =
  | 'strategy'
  | 'content'
  | 'publishing'
  | 'paid_media'
  | 'creative'
  | 'influencers'
  | 'reports'
  | 'crm'
  | 'calendar'
  | 'client_onboarding'
  | 'custom';

export type ServiceBillingInterval = 'one_time' | 'monthly' | 'quarterly' | 'yearly' | 'custom';
export type PackageBillingInterval = 'monthly' | 'quarterly' | 'yearly' | 'custom';
export type ClientPackageAssignmentStatus = 'draft' | 'active' | 'paused' | 'cancelled' | 'ended';

export interface AgencyServiceRow {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly category: string | null;
  readonly system_capability_key: SystemCapabilityKey;
  /**
   * Task 047: full capability set (primary + bridge rows). Always
   * includes `system_capability_key`. Sorted alphabetically.
   */
  readonly capability_keys: ReadonlyArray<SystemCapabilityKey>;
  readonly default_price_amount: number | string | null;
  readonly default_price_currency: string;
  readonly billing_interval: ServiceBillingInterval | null;
  readonly is_active: boolean;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListAgencyServicesData {
  readonly services: ReadonlyArray<AgencyServiceRow>;
}

export interface BootstrapAgencyServicesData {
  readonly services: ReadonlyArray<AgencyServiceRow>;
  readonly created_count: number;
  readonly capability_rows_added: number;
}

export interface UpsertAgencyServiceInput {
  readonly id?: string;
  readonly name?: string;
  readonly description?: string | null;
  readonly category?: string | null;
  readonly system_capability_key?: SystemCapabilityKey;
  /**
   * Task 047: secondary capabilities. The runtime auto-includes the
   * primary `system_capability_key` even if absent here. Absent =
   * leave existing capability set unchanged on UPDATE; present
   * (including []) = replace with primary ∪ provided.
   */
  readonly capability_keys?: ReadonlyArray<SystemCapabilityKey>;
  readonly default_price_amount?: number | null;
  readonly default_price_currency?: string;
  readonly billing_interval?: ServiceBillingInterval | null;
  readonly is_active?: boolean;
  readonly metadata?: Readonly<Record<string, unknown>>;
}

export interface UpsertAgencyServiceData {
  readonly service: AgencyServiceRow;
}

export type PricingModel =
  | 'included'
  | 'fixed'
  | 'percentage_of_spend'
  | 'fixed_plus_percentage'
  | 'per_campaign'
  | 'custom';

export type ServiceCadence =
  | 'one_time'
  | 'weekly'
  | 'monthly'
  | 'quarterly'
  | 'per_campaign'
  | 'custom';

export type DeliverableType =
  | 'design'
  | 'post'
  | 'reel'
  | 'story'
  | 'video'
  | 'campaign'
  | 'report'
  | 'meeting'
  | 'custom';

export interface PackageServiceDeliverable {
  readonly type: DeliverableType;
  readonly label: string;
  readonly quantity: number | null;
  readonly cadence: ServiceCadence | null;
  readonly notes: string | null;
}

export interface PackageServiceLine {
  readonly service_id: string;
  readonly service_name: string;
  readonly system_capability_key: SystemCapabilityKey;
  /**
   * Task 047: full capability set for this service (primary + bridge
   * rows). When listing inside a package or assignment.
   */
  readonly capability_keys: ReadonlyArray<SystemCapabilityKey>;
  readonly service_is_active: boolean;
  readonly quantity: number | string | null;
  readonly notes: string | null;
  // Task 047 commercial terms.
  readonly deliverables: ReadonlyArray<PackageServiceDeliverable>;
  readonly pricing_model: PricingModel | null;
  readonly retainer_amount: number | string | null;
  readonly retainer_currency: string;
  readonly spend_percentage: number | string | null;
  readonly cadence: ServiceCadence | null;
  readonly included: boolean;
  readonly sort_order: number;
  readonly terms_notes: string | null;
  /**
   * Task 151-A: freeform per-service-type config (social platforms +
   * quantities, ads pricing/platforms, influencer/production/content/
   * brand settings). Opaque on the wire; `package-service-config`
   * (packages/contracts) provides the typed per-service-type shapes.
   */
  readonly config: Readonly<Record<string, unknown>>;
}

export interface AgencyPackageRow {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly price_amount: number | string | null;
  readonly price_currency: string;
  readonly billing_interval: PackageBillingInterval | null;
  readonly is_active: boolean;
  readonly created_at: string;
  readonly updated_at: string;
  readonly services: ReadonlyArray<PackageServiceLine>;
}

export interface ListAgencyPackagesData {
  readonly packages: ReadonlyArray<AgencyPackageRow>;
}

export interface UpsertAgencyPackageInput {
  readonly id?: string;
  readonly name?: string;
  readonly description?: string | null;
  readonly price_amount?: number | null;
  readonly price_currency?: string;
  readonly billing_interval?: PackageBillingInterval | null;
  readonly is_active?: boolean;
  readonly metadata?: Readonly<Record<string, unknown>>;
}

export interface UpsertAgencyPackageData {
  // Note: `package` field name matches the wire envelope
  // ({ "package": { ... } }). Frontend code aliases as needed.
  readonly package: Omit<AgencyPackageRow, 'services'>;
}

export interface SetAgencyPackageServicesInput {
  readonly package_id: string;
  readonly services: ReadonlyArray<{
    readonly service_id: string;
    readonly quantity?: number | null;
    readonly notes?: string | null;
    // Task 047 commercial terms.
    readonly deliverables?: ReadonlyArray<PackageServiceDeliverable>;
    readonly pricing_model?: PricingModel | null;
    readonly retainer_amount?: number | null;
    readonly retainer_currency?: string;
    readonly spend_percentage?: number | null;
    readonly cadence?: ServiceCadence | null;
    readonly included?: boolean;
    readonly sort_order?: number;
    readonly terms_notes?: string | null;
    // Task 151-A: freeform per-service-type config object.
    readonly config?: Readonly<Record<string, unknown>>;
  }>;
}

export interface SetAgencyPackageServicesData {
  readonly composition: ReadonlyArray<{
    readonly id: string;
    readonly package_id: string;
    readonly service_id: string;
    readonly quantity: number | string | null;
    readonly notes: string | null;
    readonly deliverables: ReadonlyArray<PackageServiceDeliverable>;
    readonly pricing_model: PricingModel | null;
    readonly retainer_amount: number | string | null;
    readonly retainer_currency: string;
    readonly spend_percentage: number | string | null;
    readonly cadence: ServiceCadence | null;
    readonly included: boolean;
    readonly sort_order: number;
    readonly terms_notes: string | null;
    readonly config: Readonly<Record<string, unknown>>;
    readonly created_at: string;
  }>;
}

/**
 * Task 047 snapshot of a single included service at assignment time.
 * Stored as jsonb on `client_package_assignments.services_snapshot`.
 * Frontend should prefer this for displaying the contractual agreement
 * rather than the live-package data which can drift after edits.
 */
export interface AssignmentServiceSnapshot {
  readonly service_id: string;
  readonly service_name: string;
  readonly primary_capability_key: SystemCapabilityKey;
  readonly capability_keys: ReadonlyArray<SystemCapabilityKey>;
  readonly quantity: number | string | null;
  readonly notes: string | null;
  readonly deliverables: ReadonlyArray<PackageServiceDeliverable>;
  readonly pricing_model: PricingModel | null;
  readonly retainer_amount: number | string | null;
  readonly retainer_currency: string;
  readonly spend_percentage: number | string | null;
  readonly cadence: ServiceCadence | null;
  readonly included: boolean;
  readonly sort_order: number;
  readonly terms_notes: string | null;
}

export interface AssignmentCommercialTermsSnapshot {
  readonly package_name: string;
  readonly package_description: string | null;
  readonly package_price_amount: number | string | null;
  readonly package_price_currency: string;
  readonly package_billing_interval: PackageBillingInterval | null;
  readonly snapshot_taken_at: string;
}

export interface ClientPackageAssignmentRow {
  readonly id: string;
  readonly client_id: string;
  readonly package_id: string;
  readonly status: ClientPackageAssignmentStatus;
  readonly starts_at: string | null;
  readonly ends_at: string | null;
  readonly price_amount_snapshot: number | string | null;
  readonly price_currency_snapshot: string;
  readonly billing_interval_snapshot: PackageBillingInterval | null;
  readonly notes: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  /**
   * Live package data — current package state, may have drifted from
   * the snapshot if the package was edited after assignment. UI
   * should prefer the snapshots below for contractual display.
   */
  readonly package_name: string | null;
  readonly package_description: string | null;
  readonly package_billing_interval: string | null;
  readonly package_is_active: boolean | null;
  readonly services: ReadonlyArray<PackageServiceLine>;
  readonly capability_keys: ReadonlyArray<SystemCapabilityKey>;
  /**
   * Task 047: contractual snapshots taken at assignment time. The
   * primary truth for the client's agreement. Free-form jsonb on the
   * wire — typed here for the wizard's convenience.
   */
  readonly services_snapshot: ReadonlyArray<AssignmentServiceSnapshot>;
  readonly capability_keys_snapshot: ReadonlyArray<SystemCapabilityKey>;
  readonly commercial_terms_snapshot:
    | AssignmentCommercialTermsSnapshot
    | Readonly<Record<string, unknown>>;
}

export interface ListClientPackageAssignmentsData {
  readonly assignments: ReadonlyArray<ClientPackageAssignmentRow>;
}

export interface AssignClientPackageInput {
  readonly client_id: string;
  readonly package_id: string;
  readonly status?: ClientPackageAssignmentStatus;
  readonly starts_at?: string | null;
  readonly ends_at?: string | null;
  readonly price_amount_snapshot?: number | null;
  readonly price_currency_snapshot?: string;
  readonly billing_interval_snapshot?: PackageBillingInterval | null;
  readonly notes?: string | null;
}

export interface AssignClientPackageData {
  // The runtime now embeds package + services + capability_keys in
  // the response (Codex P1 #5 fix) so the caller can render the new
  // assignment card immediately without a follow-up
  // `list-client-package-assignments` round-trip.
  readonly assignment: ClientPackageAssignmentRow;
}

/**
 * Task 151-C — explicit status transition for an existing assignment.
 *   - `activate`: end the client's current active assignment (if any)
 *     and activate the target, atomically (one active per client).
 *   - `end`: active → 'ended'.
 *   - `cancel`: draft/paused → 'cancelled' (audit-preserving; no hard
 *     delete).
 */
export type ClientPackageAssignmentAction = 'activate' | 'end' | 'cancel';

export interface UpdateClientPackageAssignmentInput {
  readonly assignment_id: string;
  readonly action: ClientPackageAssignmentAction;
}

export interface UpdateClientPackageAssignmentData {
  readonly assignment: ClientPackageAssignmentRow;
}

// ── Task 151-D — monthly entitlement progress + carry-over ────────────

/**
 * Current-month entitlement progress for one package service. Computed
 * server-side from the immutable assignment snapshot (entitlement) + the
 * deliveries ledger (consumed). Carry-over = 1 month then expire.
 */
export interface ClientPackageServiceProgress {
  readonly service_id: string;
  readonly service_name: string;
  readonly capability_key: SystemCapabilityKey | string | null;
  /** Current period month key ('YYYY-MM', UTC). */
  readonly month: string;
  readonly entitlement: number;
  readonly carried_in: number;
  readonly consumed: number;
  readonly remaining: number;
  /** Carried-in that expires at month end if unused. */
  readonly expiring: number;
}

export interface ClientPackageAssignmentProgress {
  readonly assignment_id: string;
  readonly package_name: string | null;
  readonly month: string;
  readonly services: ReadonlyArray<ClientPackageServiceProgress>;
}

export interface GetClientPackageProgressData {
  readonly progress: ReadonlyArray<ClientPackageAssignmentProgress>;
}

export interface RecordPackageServiceDeliveryInput {
  readonly assignment_id: string;
  readonly service_id: string;
  readonly quantity?: number;
  readonly delivered_at?: string;
  readonly deliverable_type?: string | null;
  readonly notes?: string | null;
}

export interface ClientPackageServiceDeliveryRow {
  readonly id: string;
  readonly assignment_id: string;
  readonly service_id: string;
  readonly quantity: number | string;
  readonly delivered_at: string;
  readonly deliverable_type: string | null;
  readonly source: string;
  readonly notes: string | null;
  readonly created_at: string;
}

export interface RecordPackageServiceDeliveryData {
  readonly delivery: ClientPackageServiceDeliveryRow;
}

// ── list-notifications ─────────────────────────────────────────────────

export type NotificationType =
  | 'system'
  | 'task'
  | 'approval'
  | 'publishing'
  | 'billing'
  | 'crm'
  | 'paid_media'
  | 'supervised_access'
  | 'security'
  | 'report'
  | 'content';

export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface NotificationRow {
  readonly id: string;
  readonly notification_type: NotificationType;
  readonly priority: NotificationPriority;
  readonly title: string;
  readonly body: string | null;
  readonly target_type: string | null;
  readonly target_id: string | null;
  readonly client_id: string | null;
  readonly action_path: string | null;
  readonly actor_user_id: string | null;
  readonly read_at: string | null;
  readonly dismissed_at: string | null;
  readonly created_at: string;
}

export interface ListNotificationsData {
  readonly notifications: ReadonlyArray<NotificationRow>;
  readonly next_before?: string;
  readonly next_before_id?: string;
}

// ── accept-invitation ──────────────────────────────────────────────────

export interface AcceptInvitationData {
  readonly profile_id: string;
  readonly auth_user_id: string;
}

// ── signup-agency (self-serve agency owner signup, Task agency-signup-v1;
//    two-step email-ownership proof added by Task 293B) ──

/**
 * Response to `step: 'initiate'`. Deliberately carries no signal about
 * whether the address is already registered — the envelope is identical
 * for both cases, and both dispatch mail. Any future field added here
 * must preserve that.
 */
export interface SignupAgencyInitiateData {
  readonly status: 'verification_sent';
  readonly expires_in_seconds: number;
}

/** Response to `step: 'verify'` — the bootstrapped tenant's ids. */
export interface SignupAgencyData {
  readonly profile_id: string;
  readonly auth_user_id: string;
  readonly organization_id: string;
}

// ── strategy ──────────────────────────────────────────────────────────

export interface ClientStrategyRow {
  readonly id: string;
  readonly organization_id: string;
  readonly client_id: string;
  readonly initiative_id: string | null;
  readonly active_version_id: string | null;
  readonly title: string;
  readonly is_baseline: boolean;
  readonly is_active: boolean;
  readonly created_by_user_id: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListClientStrategiesData {
  readonly strategies: ReadonlyArray<ClientStrategyRow>;
}

export interface StrategyVersionRow {
  readonly id: string;
  readonly strategy_id: string;
  readonly version: number;
  readonly summary: string;
  readonly content: Readonly<Record<string, unknown>>;
  readonly ai_run_id: string | null;
  readonly created_by_user_id: string | null;
  readonly created_at: string;
}

export interface GetClientStrategyData {
  readonly strategy: ClientStrategyRow;
  readonly active_version: StrategyVersionRow | null;
}

// ── creative analysis status (Task 227 EF, wired by Task 260) ────────

/** A creative_analysis_jobs row as returned by `get-creative-analysis-status`
 *  (FE consumes a subset; extra wire fields are structurally ignored). */
export interface CreativeAnalysisJobStatusRow {
  readonly id: string;
  readonly client_id: string | null;
  readonly analysis_type: string;
  readonly creative_asset_id: string | null;
  readonly status: 'queued' | 'running' | 'succeeded' | 'failed';
  readonly error_code: string | null;
  readonly created_at: string;
  readonly finished_at: string | null;
}

/** A persisted creative_analyses row (persisted = succeeded). */
export interface CreativeAnalysisResultRow {
  readonly id: string;
  readonly client_id: string | null;
  readonly analysis_type: string;
  readonly creative_asset_id: string | null;
  readonly score: number | null;
  readonly confidence: number | null;
  readonly analyzed_at: string;
}

export interface GetCreativeAnalysisStatusData {
  readonly jobs: ReadonlyArray<CreativeAnalysisJobStatusRow>;
  readonly analyses: ReadonlyArray<CreativeAnalysisResultRow>;
}

export type StrategySuggestionStatus = 'pending' | 'accepted' | 'rejected' | 'dismissed';

export interface StrategySuggestionRow {
  readonly id: string;
  readonly strategy_id: string;
  readonly status: StrategySuggestionStatus;
  readonly title: string;
  readonly body: string;
  readonly source: string | null;
  readonly resolved_by_user_id: string | null;
  readonly resolved_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListStrategySuggestionsData {
  readonly suggestions: ReadonlyArray<StrategySuggestionRow>;
}

/**
 * Wire shape of `create-client-strategy-from-onboarding`. Mirrors what
 * the edge function returns after calling
 * `public.create_client_strategy_v1`. The `content` jsonb intentionally
 * matches the V1 contract documented in
 * `docs/frontend/050-strategy-content-contract.md`; the renderer
 * (`apps/web/src/api/strategyContent.ts`) accepts `Record<string,
 * unknown>` so we keep the wire type permissive here.
 */
export interface CreateClientStrategyFromOnboardingData {
  readonly strategy_id: string;
  readonly version_id: string;
  readonly summary: string;
  readonly content: Readonly<Record<string, unknown>>;
}

/**
 * Wire shape of `publish-client-strategy-version`. The edge function
 * delegates to `public.publish_client_strategy_version_v1` (see
 * `supabase/migrations/20260428210000_strategy_publish_version_rpc.sql`),
 * which inserts a new immutable `strategy_versions` row and atomically
 * flips `client_strategies.active_version_id`.
 */
export interface PublishClientStrategyVersionInput {
  readonly strategy_id: string;
  readonly summary: string;
  readonly content: Readonly<Record<string, unknown>>;
}

export interface PublishClientStrategyVersionData {
  readonly version_id: string;
  readonly version_number: number;
  readonly summary: string;
  readonly content: Readonly<Record<string, unknown>>;
}

// ── content ──────────────────────────────────────────────────────────

export type ContentItemStatus =
  | 'draft'
  | 'pending_client_content_approval'
  | 'client_content_approved'
  | 'in_production'
  | 'pending_internal_review'
  | 'internal_approved'
  | 'pending_client_asset_approval'
  | 'client_asset_approved'
  | 'scheduled'
  | 'published'
  | 'cancelled';

// Task 292C1 added 'writing'. Mirrors public.content_discipline. Note a
// content_item is never STORED as 'writing' (the column is immutable by
// trigger); the value discriminates the writing task.
export type ContentDiscipline = 'content' | 'design' | 'video' | 'writing';

export type ContentOrigin = 'manual' | 'ai_generated' | 'ai_assisted' | 'imported';

// Task 070 — compact embed of approval rows on list/calendar cards.
// Distinct from `ContentApprovalRow` (used by the detail panel) so the
// list endpoint can intentionally omit fields it doesn't need
// (`content_item_id`, `cardinality_mode`) without having to ship the
// full type back. Sorted newest-first by the endpoint.
export interface ContentItemApprovalEmbed {
  readonly id: string;
  readonly stage: ContentApprovalStage;
  readonly decision: ContentApprovalDecision;
  readonly reason: string | null;
  readonly approver_user_id: string;
  readonly created_at: string;
}

export interface ContentItemListRow {
  readonly id: string;
  readonly organization_id: string;
  readonly client_id: string;
  readonly initiative_id: string | null;
  readonly brief_id: string | null;
  // Task 286H — null in client-safe loader responses (AI provenance is internal,
  // Task 277); always present (non-null) for agency callers.
  readonly origin: ContentOrigin | null;
  readonly discipline: ContentDiscipline;
  readonly status: ContentItemStatus;
  readonly title: string;
  readonly ai_run_id: string | null;
  readonly created_by_user_id: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly content_approvals: ReadonlyArray<ContentItemApprovalEmbed>;
  // Task 104B Round 4 (strategic rewrite) — persisted production-
  // deliverable flag on `content_items`. Owned by the workflow:
  //   * `submit_production_task_v1` SETS true atomically with the
  //     fresh-deliverable submit + parent auto-advance.
  //   * `transition_content_item_status_v2` RESETS false on
  //     transitions that invalidate the current deliverable
  //     (`client_content_approved → in_production` to start a new
  //     cycle, any return-to-draft path).
  // The FE consumes this in `buildAdvanceActions` to choose between
  // `pending_client_content_approval` (false) and
  // `pending_client_asset_approval` (true) at `internal_approved`.
  // The DB column is `not null default false`, so the field is
  // always a boolean on the wire; if the field is somehow missing
  // (legacy API mismatch) the FE fails closed — NO final-asset
  // handoff button is shown.
  readonly has_current_production_deliverable: boolean;
}

export interface ListContentItemsData {
  readonly content_items: ReadonlyArray<ContentItemListRow>;
}

export interface ContentItemDetailRow extends ContentItemListRow {
  readonly body: string | null;
  readonly archived_at: string | null;
}

export interface GetContentItemData {
  readonly content_item: ContentItemDetailRow;
}

export type ContentVariantPlatform =
  | 'instagram_feed'
  | 'instagram_story'
  | 'instagram_reel'
  | 'facebook_post'
  | 'facebook_story'
  | 'tiktok_video'
  | 'snapchat_story';

// ── Task 279 — client operating setup (cadence config; Task 278 backend) ──
// Per-client 1:1 config. All cadence fields nullable (partial setup allowed);
// production_lead_time_days / approval_buffer_days are NOT NULL (default 0).
export interface ClientOperatingSetup {
  readonly client_id: string;
  readonly posts_per_week: number | null;
  readonly static_designs_per_week: number | null;
  readonly reels_videos_per_week: number | null;
  readonly platforms: ReadonlyArray<ContentVariantPlatform> | null;
  readonly preferred_days: ReadonlyArray<number> | null;
  readonly preferred_times_local: ReadonlyArray<string> | null;
  readonly timezone: string | null;
  readonly first_publish_date: string | null;
  readonly production_lead_time_days: number;
  readonly approval_buffer_days: number;
  readonly is_active: boolean;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface GetClientOperatingSetupData {
  readonly operating_setup: ClientOperatingSetup | null;
}

export interface UpsertClientOperatingSetupInput {
  readonly client_id: string;
  readonly posts_per_week?: number | null;
  readonly static_designs_per_week?: number | null;
  readonly reels_videos_per_week?: number | null;
  readonly platforms?: ReadonlyArray<ContentVariantPlatform> | null;
  readonly preferred_days?: ReadonlyArray<number> | null;
  readonly preferred_times_local?: ReadonlyArray<string> | null;
  readonly timezone?: string | null;
  readonly first_publish_date?: string | null;
  readonly production_lead_time_days?: number | null;
  readonly approval_buffer_days?: number | null;
  readonly is_active?: boolean | null;
}

export interface UpsertClientOperatingSetupData {
  readonly operating_setup: ClientOperatingSetup;
}

export type ContentVariantStatus = 'draft' | 'approved' | 'scheduled' | 'published' | 'failed';

// Task 105A — `list-content-variants` now embeds calendar_entries so
// the publish schedule modal can prefill from the item's planned
// scheduled_for. The shape mirrors `PendingClientApprovalCalendarEmbed`.
export interface ContentVariantCalendarEntryEmbed {
  readonly id: string;
  readonly content_variant_id: string;
  readonly scheduled_for: string;
  readonly timezone: string | null;
  readonly archived_at: string | null;
}

// Task 108B — read-only embed of the per-variant production brief
// jsonb that 108A persists into `content_variants.platform_specific`.
// Mirrors the wire shape returned by `list-content-variants` and the
// other readers (`list-pending-client-approvals` /
// `list-calendar-entries-by-month` already use the same untyped
// record shape). Kept loose on the read side so the FE stays
// forward-compatible with future brief keys without a type bump;
// the strict per-key contract lives on the WRITE side
// (`SaveContentCalendarDraftPlatformSpecific`).
export type ContentVariantPlatformSpecific = Readonly<Record<string, unknown>>;

export interface ContentVariantRow {
  readonly id: string;
  readonly content_item_id: string;
  readonly platform: ContentVariantPlatform;
  readonly status: ContentVariantStatus;
  readonly headline: string | null;
  readonly caption: string | null;
  readonly body: string | null;
  // Task 108B — `list-content-variants` now SELECTs `platform_specific`
  // so the production task panel can render the 108A production brief
  // (on_image_text / hook / script / shot_list / on_screen_text /
  // visual_direction / audio_direction / creative_brief / asset_format
  // / duration_seconds / cta / hashtags / link). The shape is jsonb on
  // the wire; the FE narrows it through `ContentVariantPlatformSpecific`.
  // Defaults to `{}` for legacy rows where the column was never written.
  readonly platform_specific: ContentVariantPlatformSpecific;
  readonly created_at: string;
  readonly updated_at: string;
  // Task 105A — non-archived calendar entries planned for this
  // variant. Empty array means no calendar entry is on file (the
  // schedule modal then falls back to tomorrow 10:00).
  readonly calendar_entries: ReadonlyArray<ContentVariantCalendarEntryEmbed>;
}

export interface ListContentVariantsData {
  readonly content_variants: ReadonlyArray<ContentVariantRow>;
}

export type ContentApprovalStage =
  | 'client_content_approval'
  | 'client_asset_approval'
  | 'internal_review';

export type ContentApprovalDecision = 'approved' | 'rejected' | 'changes_requested';

export type ContentApprovalCardinality =
  | 'any_of'
  | 'all_of'
  | 'threshold'
  | 'role_required'
  | 'ordered_steps';

export interface ContentApprovalRow {
  readonly id: string;
  readonly content_item_id: string;
  readonly stage: ContentApprovalStage;
  readonly decision: ContentApprovalDecision;
  readonly cardinality_mode: ContentApprovalCardinality;
  readonly approver_user_id: string;
  readonly reason: string | null;
  readonly created_at: string;
}

export interface ListContentApprovalsData {
  readonly content_approvals: ReadonlyArray<ContentApprovalRow>;
}

// ── Task 067: AI content calendar draft modes ────────────────────────

export type ContentCalendarMode = 'unified' | 'platform_adapted';

export interface GenerateContentCalendarDraftInput {
  readonly client_id: string;
  readonly mode: ContentCalendarMode;
  /** `YYYY-MM`. The draft fits inside this calendar month. */
  readonly month: string;
  /** 1..4 distinct publish_target_v1 values. */
  readonly platforms: ReadonlyArray<ContentVariantPlatform>;
  /** 1..30 distinct ideas. */
  readonly entry_count: number;
  /** Optional creative direction. ≤2000 chars. */
  readonly instruction?: string | null;
  /** IANA timezone (e.g. 'Asia/Riyadh'). */
  readonly timezone: string;
  /**
   * Task 284C — OPTIONAL platform-first multi-format intent (additive; validated by
   * the EF in Task 284A). The picker sends this for ANY valid selection (>= 1 resolved
   * output group), including a single format fanned across multiple targets; legacy
   * mode-based generation applies only to callers that send NO requested_outputs. When
   * present, the grouped worker (Task 284B) produces one shared asset per `asset_format`.
   * `platforms` above stays the flattened union — the legacy publish currency.
   */
  readonly requested_outputs?: ReadonlyArray<{
    readonly asset_format: 'static_image' | 'carousel' | 'vertical_video' | 'story';
    readonly targets: ReadonlyArray<ContentVariantPlatform>;
  }>;
}

// Task 108A — paired with each unified item so the per-platform
// production brief survives the unified caption-tile shape. The
// model writes one entry per requested platform; the FE
// `aiResponseToDraft` reads the matching entry per variant.
export interface ContentCalendarUnifiedPlatformBriefEntry {
  readonly platform: ContentVariantPlatform;
  readonly brief: ContentVariantProductionBrief;
}

export interface ContentCalendarUnifiedItem {
  readonly title: string;
  readonly core_idea: string;
  readonly caption: string;
  readonly hashtags: ReadonlyArray<string>;
  readonly scheduled_date: string;
  readonly scheduled_time_local: string;
  readonly rationale: string;
  // Task 108A — required by the unified worker schema. One entry
  // per requested platform so production handoff never inherits an
  // empty brief just because the operator chose unified mode.
  readonly platform_briefs: ReadonlyArray<ContentCalendarUnifiedPlatformBriefEntry>;
}

// Task 108A — production brief contract carried by every saved
// variant via `content_variants.platform_specific`. Static / image
// platforms (instagram_feed / instagram_story / snapchat_story) and
// video platforms (instagram_reel / tiktok_video) populate different
// subsets of these fields; the type intentionally keeps every field
// optional so a single shape covers both families and legacy rows
// (which carry only `hashtags` / `cta` / `link`) keep validating.
export interface ContentVariantProductionBrief {
  // Static / image-like platforms.
  readonly on_image_text?: string;
  readonly visual_direction?: string;
  readonly creative_brief?: string;
  readonly asset_format?: string;
  // Video-like platforms.
  readonly hook?: string;
  readonly script?: string;
  readonly shot_list?: string;
  readonly on_screen_text?: string;
  readonly audio_direction?: string;
  /** Positive integer in 1..3600. Video platforms only. */
  readonly duration_seconds?: number;
}

export interface ContentCalendarPlatformVariant {
  readonly platform: ContentVariantPlatform;
  readonly headline: string | null;
  readonly caption: string;
  readonly body: string | null;
  readonly hashtags: ReadonlyArray<string>;
  // Task 108A — AI-generated production brief, when the
  // platform-adapted worker filled it in. The unified worker leaves
  // this absent (the operator fills brief fields manually in the
  // DraftEditor before saving).
  readonly production_brief?: ContentVariantProductionBrief;
}

export interface ContentCalendarPlatformAdaptedItem {
  readonly title: string;
  readonly core_idea: string;
  readonly scheduled_date: string;
  readonly scheduled_time_local: string;
  readonly rationale: string;
  readonly variants: ReadonlyArray<ContentCalendarPlatformVariant>;
}

export interface ContentCalendarWarning {
  readonly code: string;
  readonly message: string;
}

export interface GenerateContentCalendarDraftDataUnified {
  readonly mode: 'unified';
  readonly ai_run_id: string;
  readonly model: string;
  readonly tokens_input: number;
  readonly tokens_output: number;
  readonly cost_usd: number;
  readonly duration_ms: number;
  readonly capability: string;
  readonly calendar_run_hash: string | null;
  readonly draft: {
    readonly mode: 'unified';
    readonly items: ReadonlyArray<ContentCalendarUnifiedItem>;
    readonly warnings: ReadonlyArray<ContentCalendarWarning>;
  };
  readonly input_echo: {
    readonly client_id: string;
    readonly mode: ContentCalendarMode;
    readonly month: string;
    readonly platforms: ReadonlyArray<ContentVariantPlatform>;
    readonly entry_count: number;
    readonly timezone: string;
  };
}

export interface GenerateContentCalendarDraftDataPlatformAdapted {
  readonly mode: 'platform_adapted';
  readonly ai_run_id: string;
  readonly model: string;
  readonly tokens_input: number;
  readonly tokens_output: number;
  readonly cost_usd: number;
  readonly duration_ms: number;
  readonly capability: string;
  readonly calendar_run_hash: string | null;
  readonly draft: {
    readonly mode: 'platform_adapted';
    readonly items: ReadonlyArray<ContentCalendarPlatformAdaptedItem>;
    readonly warnings: ReadonlyArray<ContentCalendarWarning>;
  };
  readonly input_echo: {
    readonly client_id: string;
    readonly mode: ContentCalendarMode;
    readonly month: string;
    readonly platforms: ReadonlyArray<ContentVariantPlatform>;
    readonly entry_count: number;
    readonly timezone: string;
  };
}

export type GenerateContentCalendarDraftData =
  | GenerateContentCalendarDraftDataUnified
  | GenerateContentCalendarDraftDataPlatformAdapted;

// Task 108A — extended platform_specific shape. Mirrors the
// edge-function ALLOWED_PLATFORM_SPECIFIC_KEYS allow-list and the
// SQL allow-list inside `save_content_calendar_draft_v1`. Every key
// is optional so legacy callers + manual-only forms keep working.
export interface SaveContentCalendarDraftPlatformSpecific {
  // Pre-108A — captioning + outbound link.
  readonly hashtags?: ReadonlyArray<string>;
  readonly cta?: string;
  readonly link?: string;
  // Task 108A — production brief (static / image-like platforms).
  readonly on_image_text?: string;
  readonly visual_direction?: string;
  readonly creative_brief?: string;
  readonly asset_format?: string;
  // Task 108A — production brief (video-like platforms).
  readonly hook?: string;
  readonly script?: string;
  readonly shot_list?: string;
  readonly on_screen_text?: string;
  readonly audio_direction?: string;
  /** Integer 1..3600. Server clamps. */
  readonly duration_seconds?: number;
}

export interface SaveContentCalendarDraftVariantInput {
  readonly platform: ContentVariantPlatform;
  readonly headline?: string | null;
  readonly caption?: string | null;
  readonly body?: string | null;
  readonly platform_specific?: SaveContentCalendarDraftPlatformSpecific;
}

export interface SaveContentCalendarDraftItemInput {
  readonly title: string;
  readonly body?: string | null;
  /** ISO 8601 timestamptz, must be in the future. */
  readonly scheduled_for: string;
  readonly timezone: string;
  readonly variants: ReadonlyArray<SaveContentCalendarDraftVariantInput>;
}

export interface SaveContentCalendarDraftInput {
  readonly client_id: string;
  readonly origin: 'manual' | 'ai_assisted';
  readonly ai_run_id?: string | null;
  readonly mode?: ContentCalendarMode | null;
  readonly items: ReadonlyArray<SaveContentCalendarDraftItemInput>;
}

export interface SaveContentCalendarDraftRow {
  readonly content_item_id: string;
  readonly content_variant_ids: ReadonlyArray<string>;
  readonly calendar_entry_ids: ReadonlyArray<string>;
}

export interface SaveContentCalendarDraftData {
  readonly client_id: string;
  readonly origin: 'manual' | 'ai_assisted';
  readonly mode: ContentCalendarMode | null;
  readonly ai_run_id: string | null;
  readonly items: ReadonlyArray<SaveContentCalendarDraftRow>;
}

// ── Task 292C3: writer AI generation ────────────────────────────────
// A parallel, strictly narrower version of the leadership calendar
// contract above. Runs the SAME workers (unified / platform_adapted /
// grouped) but the caller MUST be an active agency `content_writer`
// with a live non-archived assignment to an ACTIVE client
// (SECURITY.md §2.1). See `tasks/292C3-content-writer-ai-generation.md`.
//
// The generate input mirrors GenerateContentCalendarDraftInput
// verbatim: the on-the-wire shape does not change — only the server
// authorization contract does. The save input drops `origin` (the
// writer surface is inherently `ai_assisted`) and makes `ai_run_id` /
// `mode` required.

export type GenerateWriterAiDraftsInput = GenerateContentCalendarDraftInput;

export type GenerateWriterAiDraftsData = GenerateContentCalendarDraftData;

export interface SaveWriterAiCalendarDraftInput {
  readonly client_id: string;
  readonly ai_run_id: string;
  readonly mode: ContentCalendarMode;
  readonly items: ReadonlyArray<SaveContentCalendarDraftItemInput>;
}

export interface SaveWriterAiCalendarDraftRow {
  readonly content_item_id: string;
  readonly content_variant_ids: ReadonlyArray<string>;
  readonly writing_task_id: string;
  readonly writing_assignment_id: string;
  readonly calendar_entry_ids: ReadonlyArray<string>;
}

export interface SaveWriterAiCalendarDraftData {
  readonly client_id: string;
  readonly mode: ContentCalendarMode;
  readonly ai_run_id: string;
  readonly items: ReadonlyArray<SaveWriterAiCalendarDraftRow>;
}

// ── Task 068: calendar operations + review workflow ──────────────────

export interface CalendarEntryItemEmbed {
  readonly id: string;
  readonly client_id: string;
  readonly title: string;
  readonly body: string | null;
  readonly status: ContentItemStatus;
  // Task 286H — null in client-safe calendar responses (AI provenance is internal).
  readonly origin: ContentOrigin | null;
  readonly discipline: ContentDiscipline;
  readonly ai_run_id: string | null;
  readonly metadata: Readonly<Record<string, unknown>>;
  readonly created_at: string;
  // Task 070 — same compact shape as the list endpoint. Sorted
  // newest-first by the endpoint.
  readonly content_approvals: ReadonlyArray<ContentItemApprovalEmbed>;
}

export interface CalendarEntryVariantEmbed {
  readonly id: string;
  readonly content_item_id: string;
  readonly platform: ContentVariantPlatform;
  readonly status: ContentVariantStatus;
  readonly headline: string | null;
  readonly caption: string | null;
  readonly body: string | null;
  readonly platform_specific: Readonly<Record<string, unknown>>;
  readonly content_items: CalendarEntryItemEmbed | null;
}

export interface CalendarEntryRow {
  readonly id: string;
  readonly content_variant_id: string;
  readonly scheduled_for: string;
  readonly timezone: string | null;
  readonly metadata: Readonly<Record<string, unknown>>;
  readonly archived_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly content_variants: CalendarEntryVariantEmbed | null;
}

export interface ListCalendarEntriesByMonthData {
  readonly entries: ReadonlyArray<CalendarEntryRow>;
  readonly range: {
    readonly start_utc: string;
    readonly end_utc: string;
  };
}

export interface UpdateContentCalendarDraftInput {
  readonly content_item_id: string;
  readonly content_variant_id: string;
  readonly calendar_entry_id: string;
  readonly title: string;
  readonly body?: string | null;
  readonly headline?: string | null;
  readonly caption?: string | null;
  readonly variant_body?: string | null;
  /**
   * Task 289C — merge semantics: a key ABSENT here is PRESERVED on the
   * variant; an explicit `null` CLEARS it; a typed value replaces it.
   * (The RPC keeps every non-allow-list key — the production brief,
   * incl. asset_format — untouched either way.)
   */
  readonly platform_specific?: {
    readonly hashtags?: ReadonlyArray<string> | null;
    readonly cta?: string | null;
    readonly link?: string | null;
  } | null;
  readonly scheduled_for: string;
  readonly timezone: string;
}

export interface UpdateContentCalendarDraftData {
  readonly content_item_id: string;
  readonly content_variant_id: string;
  readonly calendar_entry_id: string;
  readonly status: ContentItemStatus;
}

export type ContentItemTransitionTarget =
  | 'draft'
  | 'pending_internal_review'
  | 'internal_approved'
  | 'pending_client_content_approval'
  | 'client_content_approved'
  // Task 101B — production handoff. The backend RPC v2 accepts this
  // target only from `client_content_approved` (or as an idempotent
  // retry from `in_production`); see `transition_content_item_status_v2`
  // in `20260505000000_content_workflow_production_handoff.sql`.
  | 'in_production'
  // Task 104B — final asset approval (step 5 of source-of-truth
  // workflow). `pending_client_asset_approval` is the agency's handoff
  // target after `internal_approved` (replacing the pre-104B misroute
  // to `pending_client_content_approval`). `client_asset_approved` is
  // the client's approve target. Backend RPC enforces every gate.
  | 'pending_client_asset_approval'
  | 'client_asset_approved';

export interface TransitionContentItemStatusInput {
  readonly content_item_id: string;
  readonly target_status: ContentItemTransitionTarget;
  readonly reason: string;
}

export interface TransitionContentItemStatusData {
  readonly content_item_id: string;
  readonly prev_status: ContentItemStatus;
  readonly new_status: ContentItemStatus;
  readonly approval_id: string | null;
  // Task 101B — production-handoff response fields. Non-null
  // `task_id` only when the edge resolved a production-handoff
  // transition (fresh INSERT or idempotent retry); null on every
  // other edge. `task_already_existed` is `true` on idempotent
  // retries, `false` on a fresh INSERT, and `false` (default) on
  // every non-handoff edge.
  readonly task_id: string | null;
  readonly task_already_existed: boolean;
}

// ── Task 101D — production deliverable upload ─────────────────────────
//
// All FE production-task state changes route through the three new
// edge functions. Direct PostgREST `tasks.update` from the FE is
// explicitly NOT used. The same SECURITY DEFINER RPC gate guards
// each surface.

export type TaskStatus =
  | 'open'
  | 'in_progress'
  | 'blocked'
  | 'submitted'
  | 'completed'
  | 'cancelled';

export interface PrepareProductionTaskUploadInput {
  readonly task_id: string;
  readonly filename: string;
  readonly mime_type: string;
  readonly byte_size: number;
}

export interface PrepareProductionTaskUploadData {
  readonly upload_url: string;
  readonly upload_url_expires_at: string;
  readonly storage_key: string;
  readonly bucket: 'kmi-task-files';
  readonly required_headers: { readonly 'Content-Type': string };
}

export interface StartProductionTaskInput {
  readonly task_id: string;
}

export interface StartProductionTaskData {
  readonly task_id: string;
  readonly prev_status: TaskStatus;
  readonly new_status: TaskStatus;
  readonly task_already_started: boolean;
}

export interface SubmitProductionTaskInput {
  readonly task_id: string;
  readonly storage_key: string;
  readonly title: string;
  readonly mime_type: string;
  readonly summary?: string;
}

export interface SubmitProductionTaskData {
  readonly task_id: string;
  readonly task_file_id: string;
  readonly task_submission_id: string;
  readonly prev_status: TaskStatus;
  readonly new_status: TaskStatus;
  readonly task_already_submitted: boolean;
}

// Task 215 — designer clarify / block workflow (assignee-gated SD RPCs).
export interface BlockProductionTaskInput {
  readonly task_id: string;
  readonly reason: string;
}

export interface BlockProductionTaskData {
  readonly task_id: string;
  readonly prev_status: TaskStatus;
  readonly new_status: TaskStatus;
  readonly task_already_blocked: boolean;
}

export interface RequestProductionTaskClarificationInput {
  readonly task_id: string;
  readonly message: string;
}

export interface RequestProductionTaskClarificationData {
  readonly task_id: string;
  readonly notified_count: number;
}

// ── Task 220 backend — content-writer drafts + senior review (UI: Task 219) ──
export type ContentWriterDraftStatus =
  | 'drafting'
  | 'submitted_to_senior'
  | 'returned_with_notes'
  | 'approved';

export interface ContentWriterDraftItemEmbed {
  readonly title: string;
  readonly discipline: ContentDiscipline;
  readonly status: ContentItemStatus;
  readonly origin: ContentOrigin;
}
export interface ContentWriterDraftVariantEmbed {
  readonly platform: ContentVariantPlatform;
}
export interface ContentWriterDraftRow {
  readonly id: string;
  readonly status: ContentWriterDraftStatus;
  readonly content_variant_id: string;
  readonly content_item_id: string;
  readonly client_id: string | null;
  readonly assignee_user_id: string;
  readonly headline: string | null;
  readonly caption: string | null;
  readonly body: string | null;
  readonly platform_specific: ContentVariantPlatformSpecific;
  readonly senior_notes: string | null;
  readonly submitted_at: string | null;
  readonly reviewed_at: string | null;
  readonly updated_at: string;
  readonly content_items: ContentWriterDraftItemEmbed | null;
  readonly content_variants: ContentWriterDraftVariantEmbed | null;
  /**
   * Task 292C2 — display fields the review queue needs and could not derive.
   * Both are resolved server-side under the CALLER's RLS and are `null` when
   * the caller may not read the underlying row, so neither is a directory.
   */
  readonly client_name: string | null;
  readonly assignee_name: string | null;
}
export interface ListContentWriterDraftsData {
  readonly drafts: ReadonlyArray<ContentWriterDraftRow>;
}
export interface SaveContentWriterDraftInput {
  readonly content_variant_id: string;
  readonly headline?: string | null;
  readonly caption?: string | null;
  readonly body?: string | null;
  readonly platform_specific?: Readonly<Record<string, unknown>>;
}
export interface SaveContentWriterDraftData {
  readonly draft_id: string;
  readonly status: ContentWriterDraftStatus;
}
/**
 * Task 292B3 — the submit payload now carries the writer's CURRENT editor
 * buffers, and the endpoint saves + submits them in one server transaction.
 * Sending only `content_variant_id` still submits whatever was last saved
 * (the pre-292B3 behaviour), which is what a plain re-submit wants.
 *
 * `platform_specific` is forwarded to the RPC AS-IS — do not normalise or
 * filter it here; the writer-owned-key contract is applied at promotion time
 * (Task 292B2).
 */
export interface SubmitContentWriterDraftInput {
  readonly content_variant_id: string;
  readonly headline?: string | null;
  readonly caption?: string | null;
  readonly body?: string | null;
  readonly platform_specific?: ContentVariantPlatformSpecific;
  /**
   * The canonical `content_variants.updated_at` the editor was hydrated from.
   * REQUIRED whenever a payload is sent: the server rejects a mismatch with a
   * 409 before writing, so a concurrent canonical edit is never clobbered.
   */
  readonly base_variant_updated_at?: string;
}
export interface SubmitContentWriterDraftData {
  readonly draft_id: string;
  readonly status: ContentWriterDraftStatus;
  readonly notified_count: number;
}

// ── Task 292C1 — the pre-client writing lane ────────────────────────

/** One writer the current caller may hand this content item to. */
export interface EligibleContentWriter {
  readonly user_id: string;
  readonly full_name: string;
  /** Already the active assignee of this item's writing task. */
  readonly is_currently_assigned: boolean;
}
export interface ListEligibleContentWritersData {
  /** Empty is a legitimate answer (no eligible writer on this client). */
  readonly writers: ReadonlyArray<EligibleContentWriter>;
}

export interface SendContentItemToWritingInput {
  readonly content_item_id: string;
  readonly assignee_user_id: string;
  readonly note?: string | null;
}
export interface SendContentItemToWritingData {
  readonly task_id: string;
  readonly assignment_id: string;
  readonly assignee_user_id: string;
  /** The writing task was reopened / reused rather than created. */
  readonly task_already_existed: boolean;
  /** The writer was already bound: the call was an idempotent no-op re-send. */
  readonly assignment_already_existed: boolean;
}
export type ContentWriterReviewDecision =
  | 'return'
  | 'approve_internal'
  | 'approve_and_send_to_client';
export interface ReviewContentWriterDraftInput {
  readonly draft_id: string;
  readonly decision: ContentWriterReviewDecision;
  readonly notes?: string | null;
}
export interface ReviewContentWriterDraftData {
  readonly draft_id: string;
  readonly content_item_id: string;
  readonly draft_status: ContentWriterDraftStatus;
  readonly item_status: ContentItemStatus;
  readonly sent_to_client: boolean;
}

// Read-only lookup that powers the inline production-task panel
// inside ContentPage. Returns at most one non-cancelled task per
// content_item; the array shape leaves room for legacy multi-task
// states without changing the wire shape.
export interface ProductionTaskRow {
  readonly id: string;
  readonly organization_id: string;
  readonly client_id: string | null;
  readonly source_type: string;
  readonly source_id: string;
  readonly status: TaskStatus;
  readonly title: string;
  readonly description: string | null;
  readonly due_at: string | null;
  readonly metadata: Readonly<Record<string, unknown>>;
  readonly created_at: string;
  readonly updated_at: string;
  // Active human assignee on the task (if one exists). Used by the
  // panel to gate action buttons to the actual worker. Null when no
  // active human assignment row exists.
  readonly active_assignee_user_id: string | null;
  // Codex 101D-2 P2: active AI worker assignment (if one exists).
  // The schema's `task_assignments_xor_assignee` CHECK enforces
  // exactly one of (assignee_user_id, ai_worker_id) per active row,
  // so a task with `active_ai_worker_id !== null` is owned by an
  // automated worker — the human self-claim affordance must NOT
  // render for it (the start RPC would reject).
  readonly active_ai_worker_id: string | null;
  // Most recent task_submissions row's `created_at`, or null when
  // the task has not been submitted yet. Used by the panel to show
  // "submitted at <time>" instead of buttons after handoff.
  readonly latest_submission_at: string | null;
  // Task 164 — latest produced deliverable for this task (signed-URL
  // preview), or null when the task has no submitted/visible
  // deliverable. Surfaced so the agency content detail can render
  // the real produced asset INSIDE the platform-card MediaPreviewSlot
  // instead of falling back to the discipline placeholder
  // (`'محتوى كتابي'` / `'لا يوجد تصميم بعد'`). Mirrors the embed
  // shape that `list-pending-client-approvals` already returns for
  // the client final-approval inbox.
  readonly latest_production_deliverable: ProductionTaskDeliverableEmbed | null;
}

export interface ProductionTaskDeliverableEmbed {
  readonly task_id: string;
  readonly task_file_id: string;
  readonly task_submission_id: string;
  readonly title: string;
  readonly mime_type: string;
  readonly byte_size: number | null;
  readonly submitted_at: string;
  readonly summary: string | null;
  readonly signed_url: string;
  readonly signed_url_expires_at: string;
}

export interface ListProductionTasksForContentItemData {
  readonly tasks: ReadonlyArray<ProductionTaskRow>;
}

// ── Task 117 / TEAM-2: production routing ─────────────────────────────
// Wire shapes for `assign-production-task` (manual assign / reassign by
// owner / marketing_manager / production_manager-with-CAT) and
// `list-eligible-production-assignees` (returns the augmented CAT
// roster + active assignee + discipline for the production-panel
// picker / empty state).
//
// Both EFs are RPC-only thin wrappers. The wire shapes here mirror
// `packages/contracts/src/production-routing.ts` byte-for-byte (the
// `@kmi/contracts` package is the source of truth; this file
// re-declares the shapes locally because `apps/web/src/api/types.ts`
// is the historical types entry-point for the FE — every other
// production-task contract is declared here, so production routing
// follows the same convention).

/** §5.0 D7 — auto-assign decision discriminator (server-internal). */
export type AutoAssignDecision =
  | 'auto_assigned'
  | 'no_eligibles'
  | 'multiple_eligibles'
  | 'already_assigned'
  | 'task_not_eligible';

/** `'manual'` for fresh assigns / reassigns; `'auto_on_handoff'` for
 *  the server-internal v2 hook; `'unchanged'` for the idempotent
 *  "target was already the active assignee" path. */
export type ProductionTaskAssignmentSource = 'manual' | 'auto_on_handoff' | 'unchanged';

/** §5.0 D8 — the `conflict` envelope's `detail` value when the assign
 *  EF rejects a call against a workflow-terminal task. The FE matches
 *  on this exact string to render the right Arabic copy. */
export const ASSIGN_PRODUCTION_TASK_CONFLICT_TERMINAL_STATE = 'task_in_terminal_state';

export interface AssignProductionTaskInput {
  readonly task_id: string;
  readonly assignee_user_id: string;
  /** Optional free-text reason, ≤ 2000 chars; rejected if it contains
   *  credential anchors. */
  readonly reason?: string | null;
}

export interface AssignProductionTaskData {
  readonly task_id: string;
  /** The assignment row that was just ended, or `null` if this is the
   *  first assignment for the task. */
  readonly previous_assignment_id: string | null;
  /** The new active assignment row. Equal to `previous_assignment_id`
   *  when `already_assigned=true`. */
  readonly new_assignment_id: string;
  /** Previous active assignee, or `null`. */
  readonly previous_assignee_user_id: string | null;
  readonly assignment_source: ProductionTaskAssignmentSource;
  /** `true` when the call was an idempotent no-op (target was already
   *  the active assignee). */
  readonly already_assigned: boolean;
}

export interface ListEligibleProductionAssigneesInput {
  readonly task_id: string;
}

/** A single agency role on an eligible-assignee row. Mirrors
 *  `ClientTeamMemberRole` (agency-category only). */
export interface EligibleAssigneeRole {
  readonly id: string;
  readonly key: string;
  readonly category: 'agency';
  readonly name_en: string | null;
  readonly name_ar: string | null;
}

/** The profile summary embedded in an eligible-assignee row. */
export interface EligibleAssigneeProfile {
  readonly auth_user_id: string;
  readonly email: string;
  readonly full_name: string | null;
  readonly avatar_url: string | null;
  readonly is_active: boolean;
}

/** One CAT member rendered in the production-panel picker. Extends
 *  `ClientTeamMember` with `is_eligible_for_this_task`. */
export interface EligibleAssignee {
  readonly assignment_id: string;
  readonly user_id: string;
  readonly assigned_at: string;
  readonly assigned_by_user_id: string | null;
  readonly profile: EligibleAssigneeProfile | null;
  readonly roles: ReadonlyArray<EligibleAssigneeRole>;
  /** Disciplines this member is eligible to execute (union of the
   *  role-derived map). */
  readonly disciplines: ReadonlyArray<ContentDiscipline>;
  /** Exact eligibility (matches the assign RPC's gate — honors custom-deny). */
  readonly is_eligible_for_this_task: boolean;
}

export interface ListEligibleProductionAssigneesData {
  readonly task_id: string;
  readonly client_id: string;
  readonly discipline: ContentDiscipline;
  readonly active_assignee_user_id: string | null;
  readonly members: ReadonlyArray<EligibleAssignee>;
  readonly eligible_count: number;
}

// ── Task 118 — Employee Workspace v1: list my production tasks ──────

/**
 * Status of the underlying `tasks` row for a production assignment.
 * Excludes terminal `completed` / `cancelled` — the EF filters them
 * out so the caller only ever sees live work.
 */
export type MyProductionTaskStatus = 'open' | 'in_progress' | 'submitted' | 'blocked';

export interface MyProductionTaskClient {
  readonly id: string;
  readonly name: string;
}

export interface MyProductionTaskContentItem {
  readonly id: string;
  readonly title: string;
  readonly status: ContentItemStatus;
  readonly discipline: ContentDiscipline;
}

export interface MyProductionTask {
  readonly assignment_id: string;
  readonly task_id: string;
  readonly task_status: MyProductionTaskStatus;
  readonly task_created_at: string;
  /**
   * Metadata persisted on the active `task_assignments` row.
   * `assignment_source` is the load-bearing field: `'auto_on_handoff'`
   * for routing-RPC auto-assigns, `'manual'` for owner / MM /
   * PM-with-CAT explicit assigns. Other keys (discipline, reason)
   * round-trip from the same metadata jsonb.
   */
  readonly assignment_metadata: Readonly<Record<string, unknown>>;
  /**
   * The parent client embed. `null` only in the degraded case where
   * the assignee can read the assignment but not the client (RLS
   * drift) — the EF surfaces the row so the assignee sees that work
   * exists, but without parent context. In normal V1 flow the
   * production-routing RPCs guarantee CAT membership so this is
   * always populated.
   */
  readonly client: MyProductionTaskClient | null;
  /**
   * The parent content_item embed. Same `null`-on-RLS-drift
   * semantics as `client` above.
   */
  readonly content_item: MyProductionTaskContentItem | null;
}

export interface ListMyProductionTasksData {
  readonly tasks: ReadonlyArray<MyProductionTask>;
}

// ── Task 069: client approval inbox ──────────────────────────────────

export interface PendingClientApprovalCalendarEmbed {
  readonly id: string;
  readonly content_variant_id: string;
  readonly scheduled_for: string;
  readonly timezone: string | null;
  readonly archived_at: string | null;
}

export interface PendingClientApprovalVariantEmbed {
  readonly id: string;
  readonly content_item_id: string;
  readonly platform: ContentVariantPlatform;
  readonly status: ContentVariantStatus;
  readonly headline: string | null;
  readonly caption: string | null;
  readonly body: string | null;
  readonly platform_specific: Readonly<Record<string, unknown>>;
  readonly archived_at: string | null;
  readonly calendar_entries: ReadonlyArray<PendingClientApprovalCalendarEmbed>;
}

// Task 104B Round 2 — wire shape of the latest produced deliverable
// attached to each asset-stage inbox row. NEVER includes `storage_key`;
// the only path the browser uses to read the file is the short-lived
// `signed_url` (re-fetched on every inbox open).
export interface PendingClientApprovalDeliverable {
  readonly task_id: string;
  readonly task_status: TaskStatus;
  readonly task_file_id: string;
  readonly task_submission_id: string;
  readonly title: string;
  readonly mime_type: string | null;
  // V1 returns null — the bucket-metadata roundtrip is deferred. Shape
  // is widened now so future revisions can backfill without breaking
  // the FE consumer.
  readonly byte_size: number | null;
  readonly submitted_at: string;
  readonly summary: string | null;
  readonly signed_url: string;
  readonly signed_url_expires_at: string;
}

export interface PendingClientApprovalRow {
  readonly id: string;
  readonly organization_id: string;
  readonly client_id: string;
  readonly title: string;
  readonly body: string | null;
  readonly status: ContentItemStatus;
  // Task 286H — null in the client-safe inbox response (AI provenance is internal).
  readonly origin: ContentOrigin | null;
  readonly discipline: ContentDiscipline;
  readonly ai_run_id: string | null;
  readonly created_by_user_id: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly content_variants: ReadonlyArray<PendingClientApprovalVariantEmbed>;
  // Task 104B Round 2 — always present on the wire. Non-null only on
  // `stage=asset` rows that have a visible submitted (or completed)
  // production task with a task_files row. Null on `stage=content`
  // rows AND on `stage=asset` rows with no visible deliverable (the
  // FE blocks approval in that case — approving a missing asset is
  // invalid).
  readonly latest_production_deliverable: PendingClientApprovalDeliverable | null;
}

// Task 104B — `stage` selects which client-side approval queue to fetch.
//   'content' (default) — pre-production approval (the original Task 069
//                         behavior; preserved for back-compat).
//   'asset'             — final produced-asset approval (the deliverable
//                         submitted via `submit_production_task_v1` and
//                         internally approved by the agency).
// Shape is identical for both stages.
export type PendingClientApprovalStage = 'content' | 'asset';

export interface ListPendingClientApprovalsData {
  readonly items: ReadonlyArray<PendingClientApprovalRow>;
  // Task 104B — echoed by the edge function so the FE can label the
  // queue without re-parsing the URL it sent.
  readonly stage: PendingClientApprovalStage;
}

// ── paid media ───────────────────────────────────────────────────────

export type PaidMediaPlatform = 'meta_ads' | 'google_ads' | 'tiktok_ads' | 'snapchat_ads';

export type PaidMediaEntityStatus = 'active' | 'paused' | 'completed' | 'archived' | 'unknown';

export type AdAccountStatus = 'active' | 'reauthorization_required' | 'disabled' | 'archived';

/**
 * Health enum of the backing `integration_connections` row (IC-2).
 * Mirrors the locked `@kmi/contracts` `INTEGRATION_CONNECTION_STATUSES`
 * (`active | reauthorization_required | revoked | expired`). Surfaced
 * onto `AdAccountRow` by the `list-ad-accounts` EF (Task 152) so the
 * paid-media connect card can distinguish a healthy Meta connection from
 * one that needs re-authorization.
 */
export type IntegrationConnectionStatus =
  | 'active'
  | 'reauthorization_required'
  | 'revoked'
  | 'expired';

export interface AdAccountRow {
  readonly id: string;
  readonly organization_id: string;
  readonly client_id: string;
  readonly platform: PaidMediaPlatform;
  readonly account_external_id: string;
  readonly account_name: string;
  readonly currency_code: string | null;
  readonly timezone: string | null;
  readonly status: AdAccountStatus;
  readonly connected_by_user_id: string | null;
  readonly last_synced_at: string | null;
  readonly archived_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  /**
   * Task 145 (IC-4) — FK to the `integration_connections` row that
   * materialised this ad_account. `null` for legacy rows that pre-
   * date IC-3 / IC-4, or for ad_accounts whose source credential was
   * later deleted (`ON DELETE SET NULL`). The FE uses this to render
   * a "connected" state on the paid-media page instead of the
   * connect CTA.
   */
  readonly integration_connection_id: string | null;
  /**
   * Task 152 — health status of the linked `integration_connections`
   * row, surfaced by `list-ad-accounts` (status-only; no token columns).
   * `null` when the account is unlinked OR the IC row is invisible under
   * RLS. **Optional** — `undefined` during a wire-version skew (FE
   * deployed before the EF), which the connect card treats as "do not
   * show connected" rather than a false reauth prompt.
   */
  readonly integration_connection_status?: IntegrationConnectionStatus | null;
}

export interface ListAdAccountsData {
  readonly accounts: ReadonlyArray<AdAccountRow>;
  readonly next_cursor: string | null;
}

export interface PaidCampaignRow {
  readonly id: string;
  readonly organization_id: string;
  readonly client_id: string;
  readonly ad_account_id: string;
  readonly initiative_id: string | null;
  readonly platform_campaign_id: string;
  readonly name: string;
  readonly status: PaidMediaEntityStatus;
  readonly objective: string | null;
  readonly buying_type: string | null;
  readonly started_at: string | null;
  readonly ended_at: string | null;
  readonly last_synced_at: string | null;
  readonly archived_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  /** Flattened from `ad_accounts.platform`. May be null when the actor lacks paid_media.account.read. */
  readonly platform: PaidMediaPlatform | null;
}

export interface ListPaidMediaCampaignsData {
  readonly campaigns: ReadonlyArray<PaidCampaignRow>;
  readonly next_cursor: string | null;
}

/**
 * Task 148 (IC-5 MVP) — IC-5 wire shapes live in `@kmi/contracts`
 * (Task 148 §2.6) as the source-of-truth so FE + Worker + Edge
 * consumers share one definition. The web `api/types` module re-
 * exports them so the legacy import path stays stable.
 *
 * `ListClientPaidCampaignsData` is identical in shape to
 * `ListPaidMediaCampaignsData` so the client paid-media page can
 * switch endpoints without changing its render code. The EF returns
 * `platform: 'meta_ads'` on every row in V1 (Meta-only provider),
 * so the `ClientPaidCampaignRow.platform` field is always populated
 * for client-category readers (no `null` fallback unlike the agency-
 * side `PaidCampaignRow` which can surface null when the actor lacks
 * `paid_media.account.read`).
 */
export type {
  SyncPaidMediaMetaCampaignsInput,
  SyncPaidMediaMetaCampaignsData,
  ListClientPaidCampaignsInput,
  ListClientPaidCampaignsData,
  ClientPaidCampaignRow,
} from '@kmi/contracts';

/**
 * Task 155 (IC-5.3) — the manual Meta sync now ingests the full hierarchy
 * + daily insights, so the trigger EF forwards a PER-ENTITY breakdown on
 * top of the Task-148 4-field summary. These per-entity fields are
 * OPTIONAL: an older EF/Worker (or a phased-deploy version skew) returns
 * only the base 4 fields, and the FE defaults the missing counts to zero
 * (see `toSyncSummary` in PaidMediaSyncMetaCampaignsButton). Defined here
 * (web-local) — NOT in `packages/contracts` — so the `@kmi/contracts`
 * base stays the minimal source-of-truth and this PR leaves the
 * services/packages layer untouched.
 */
export interface PaidMediaSyncEntityCounts {
  readonly inserted: number;
  readonly updated: number;
}

export interface PaidMediaSyncSummaryData {
  readonly sync_run_id: string;
  readonly status: 'succeeded' | 'partial' | 'failed';
  // Aggregate totals across every entity.
  readonly rows_inserted: number;
  readonly rows_updated: number;
  // IC-5.3 per-entity breakdown (optional — defaulted to zero if absent).
  readonly campaigns?: PaidMediaSyncEntityCounts;
  readonly ad_sets?: PaidMediaSyncEntityCounts;
  readonly ads?: PaidMediaSyncEntityCounts;
  readonly creatives?: PaidMediaSyncEntityCounts;
  readonly daily_insights?: PaidMediaSyncEntityCounts;
  readonly partial?: boolean;
}

/**
 * IC-7.4 (Task 195) — Snapchat manual-sync summary. Web-local twin of
 * `PaidMediaSyncSummaryData` (the rich shape isn't in `@kmi/contracts`). The
 * Snapchat trigger EF forwards the Snapchat Worker's per-entity breakdown,
 * which uses `ad_squads` (NOT `ad_sets`) and has NO `creatives` stream (phase 1
 * is campaign grain). Per-entity fields are OPTIONAL (version-skew safe; the FE
 * defaults missing counts to zero).
 */
export interface SnapchatPaidMediaSyncSummaryData {
  readonly sync_run_id: string;
  readonly status: 'succeeded' | 'partial' | 'failed';
  readonly rows_inserted: number;
  readonly rows_updated: number;
  readonly campaigns?: PaidMediaSyncEntityCounts;
  readonly ad_squads?: PaidMediaSyncEntityCounts;
  readonly ads?: PaidMediaSyncEntityCounts;
  readonly daily_insights?: PaidMediaSyncEntityCounts;
  readonly partial?: boolean;
}

export interface GetPaidMediaCampaignData {
  readonly campaign: PaidCampaignRow;
}

export interface AdSetRow {
  readonly id: string;
  readonly paid_campaign_id: string;
  readonly platform_ad_set_id: string;
  readonly name: string;
  readonly status: PaidMediaEntityStatus;
  readonly optimization_goal: string | null;
  readonly billing_event: string | null;
  readonly started_at: string | null;
  readonly ended_at: string | null;
  readonly last_synced_at: string | null;
  readonly archived_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly platform: PaidMediaPlatform | null;
}

export interface ListPaidMediaAdSetsData {
  readonly ad_sets: ReadonlyArray<AdSetRow>;
  readonly next_cursor: string | null;
}

export interface AdRow {
  readonly id: string;
  readonly ad_set_id: string;
  readonly paid_campaign_id: string | null;
  readonly platform_ad_id: string;
  readonly name: string;
  readonly status: PaidMediaEntityStatus;
  readonly creative_external_id: string | null;
  readonly content_variant_id: string | null;
  readonly last_synced_at: string | null;
  readonly archived_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly platform: PaidMediaPlatform | null;
}

export interface ListPaidMediaAdsData {
  readonly ads: ReadonlyArray<AdRow>;
  readonly next_cursor: string | null;
}

export type PaidMediaMetricLevel = 'campaign' | 'ad_set' | 'ad';

export interface CampaignMetricRow {
  readonly id: string;
  readonly organization_id: string;
  readonly client_id: string;
  readonly ad_account_id: string;
  readonly paid_campaign_id: string;
  readonly ad_set_id: string | null;
  readonly ad_id: string | null;
  readonly metric_level: PaidMediaMetricLevel;
  readonly window_start: string;
  readonly window_end: string;
  readonly currency_code: string | null;
  readonly spend: number;
  readonly impressions: number;
  readonly reach: number;
  readonly clicks: number;
  readonly link_clicks: number;
  readonly conversions: number;
  readonly conversion_value: number;
  readonly ctr: number | null;
  readonly cpc: number | null;
  readonly cpm: number | null;
  readonly cvr: number | null;
  readonly roas: number | null;
  readonly created_at: string;
}

export interface ListPaidMediaMetricsData {
  readonly metrics: ReadonlyArray<CampaignMetricRow>;
  readonly next_cursor: string | null;
}

// ── paid-media performance analytics summary (Task 157) ───────────────
// Wire shape of `get-paid-media-performance-summary` — aggregated KPIs +
// daily time-series + top-N per-level breakdown for one campaign + window.
export interface PaidMediaPerformanceKpis {
  readonly spend: number;
  readonly impressions: number;
  readonly reach: number;
  readonly clicks: number;
  readonly link_clicks: number;
  readonly conversions: number;
  readonly conversion_value: number;
  readonly ctr: number | null;
  readonly cpc: number | null;
  readonly cpm: number | null;
  readonly cpa: number | null;
  readonly roas: number | null;
  readonly cvr: number | null;
}

export interface PaidMediaPerformanceSeriesPoint {
  readonly metric_date: string;
  readonly spend: number;
  readonly impressions: number;
  readonly reach: number;
  readonly clicks: number;
  readonly link_clicks: number;
  readonly conversions: number;
  readonly conversion_value: number;
  readonly ctr: number | null;
  readonly roas: number | null;
}

export interface PaidMediaPerformanceBreakdownEntry {
  readonly level: PaidMediaMetricLevel;
  readonly entity_id: string;
  readonly name: string | null;
  readonly spend: number;
  readonly impressions: number;
  readonly clicks: number;
  readonly conversions: number;
  readonly conversion_value: number;
  readonly ctr: number | null;
  readonly cpc: number | null;
  readonly cpm: number | null;
  readonly cpa: number | null;
  readonly roas: number | null;
  readonly cvr: number | null;
}

export interface PaidMediaPerformanceSummaryData {
  readonly currency_code: string | null;
  readonly range: { readonly since: string; readonly until: string };
  readonly kpis: PaidMediaPerformanceKpis;
  readonly series: ReadonlyArray<PaidMediaPerformanceSeriesPoint>;
  readonly breakdown: {
    readonly campaign: ReadonlyArray<PaidMediaPerformanceBreakdownEntry>;
    readonly ad_set: ReadonlyArray<PaidMediaPerformanceBreakdownEntry>;
    readonly ad: ReadonlyArray<PaidMediaPerformanceBreakdownEntry>;
  };
  readonly partial: boolean;
}

// ── creative intelligence ────────────────────────────────────────────

export type CreativeAssetType =
  | 'image'
  | 'video'
  | 'audio'
  | 'copy_block'
  | 'document'
  | 'multipart';

export interface CreativeAssetRow {
  readonly id: string;
  readonly organization_id: string;
  readonly client_id: string | null;
  readonly asset_type: CreativeAssetType;
  readonly canonical_name: string;
  readonly aliases: ReadonlyArray<string>;
  readonly mime_type: string | null;
  readonly byte_size: number | null;
  readonly checksum_sha256: string | null;
  readonly width: number | null;
  readonly height: number | null;
  readonly duration_ms: number | null;
  readonly uploaded_by_user_id: string | null;
  readonly archived_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListCreativeAssetsData {
  readonly assets: ReadonlyArray<CreativeAssetRow>;
}

// Task 221 — cross-platform content groups (read-only). Mirrors the EF wire
// shape produced by `list-creative-intelligence-content-groups`.
export type CreativeIntelligencePlatform = 'meta_ads' | 'snapchat_ads' | 'tiktok_ads';
export type CreativeIntelligencePlatformAvailability =
  | 'has_data'
  | 'no_data'
  | 'not_connected'
  | 'pending_activation';
export type CreativeIntelligencePerformanceSignal = 'winner' | 'stable' | 'weak' | 'waste';
export type CreativeIntelligenceFatigueSignal = 'none' | 'watch' | 'fatigued';
export type CreativeIntelligenceMatchQuality =
  | 'asset_match'
  | 'exact_name'
  | 'alias'
  | 'fuzzy'
  | 'unmatched';
export type CreativeIntelligenceRecommendationKind =
  | 'scale_winner'
  | 'refresh_fatigued'
  | 'adapt_platform'
  | 'stop_waste'
  | 'needs_more_data';

export interface CreativeIntelligencePlatformSlice {
  readonly platform: CreativeIntelligencePlatform;
  readonly availability: CreativeIntelligencePlatformAvailability;
  readonly matched_ads_count: number;
  readonly spend: number;
  readonly impressions: number;
  readonly clicks_or_swipes: number;
  readonly video_views: number | null;
  readonly conversions: number;
  readonly conversion_value: number;
  readonly ctr: number | null;
  readonly cpa: number | null;
  readonly roas: number | null;
  readonly fatigue_signal: CreativeIntelligenceFatigueSignal;
  readonly performance_signal: CreativeIntelligencePerformanceSignal;
}

export interface CreativeIntelligenceRecommendation {
  readonly kind: CreativeIntelligenceRecommendationKind;
  readonly reason: string;
  readonly evidence: ReadonlyArray<string>;
}

export interface CreativeIntelligenceContentGroup {
  readonly id: string;
  readonly client_id: string;
  readonly display_name: string;
  readonly creative_asset_id: string | null;
  readonly match_quality: CreativeIntelligenceMatchQuality;
  readonly platforms: ReadonlyArray<CreativeIntelligencePlatformSlice>;
  readonly aggregate: {
    readonly spend: number;
    readonly impressions: number;
    readonly conversions: number;
    readonly conversion_value: number;
    readonly best_platform: CreativeIntelligencePlatform | null;
    readonly worst_platform: CreativeIntelligencePlatform | null;
  };
  readonly recommendations: ReadonlyArray<CreativeIntelligenceRecommendation>;
}

export interface ListCreativeIntelligenceContentGroupsData {
  readonly client_id: string;
  readonly range: { readonly date_from: string; readonly date_to: string };
  readonly content_groups: ReadonlyArray<CreativeIntelligenceContentGroup>;
  readonly partial: boolean;
}

export interface GetCreativeAssetData {
  readonly asset: CreativeAssetRow;
}

export type CreativeAssetMatchSource =
  | 'paid_media_ad'
  | 'paid_media_ad_set'
  | 'paid_media_campaign'
  | 'content_item'
  | 'content_variant'
  | 'external_text';

export type CreativeAssetMatchMethod = 'exact_name' | 'alias' | 'fuzzy' | 'manual_link';

export interface CreativeAssetMatchRow {
  readonly id: string;
  readonly organization_id: string;
  readonly client_id: string | null;
  readonly creative_asset_id: string;
  readonly match_source: CreativeAssetMatchSource;
  readonly match_source_id: string | null;
  readonly match_source_external_name: string;
  readonly match_method: CreativeAssetMatchMethod;
  readonly match_confidence: number | null;
  readonly matched_by_user_id: string | null;
  readonly archived_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListCreativeAssetMatchesData {
  readonly matches: ReadonlyArray<CreativeAssetMatchRow>;
}

// ── creative-intelligence asset ↔ paid-media match review (Task 222 B1) ──

/** Suggestion methods proposed by `suggest-creative-asset-paid-matches`. The
 *  WRITE method (`manual_link`) is not here — it is set by the link endpoint. */
export type CreativeAssetPaidMatchMethod = 'exact_name' | 'alias' | 'fuzzy';

export type CreativeAssetPaidMatchPlatformConnection =
  | 'connected'
  | 'not_connected'
  | 'pending_activation';

export interface CreativeAssetPaidMatchSuggestion {
  readonly ad_id: string;
  readonly ad_name: string;
  readonly platform: CreativeIntelligencePlatform;
  readonly match_method: CreativeAssetPaidMatchMethod;
  /** 1 for exact/alias; the normalized similarity (0..1) for fuzzy. */
  readonly confidence: number;
}

export interface CreativeAssetExistingPaidMatch {
  readonly id: string;
  readonly ad_id: string;
  readonly ad_name: string;
  /** Resolved when the linked ad has insights in the window; null otherwise. */
  readonly platform: CreativeIntelligencePlatform | null;
  readonly match_method: CreativeAssetMatchMethod;
  readonly match_confidence: number | null;
}

export interface CreativeAssetUnmatchedAd {
  readonly ad_id: string;
  readonly ad_name: string;
  readonly platform: CreativeIntelligencePlatform;
}

export interface SuggestCreativeAssetPaidMatchesData {
  readonly asset: {
    readonly id: string;
    readonly canonical_name: string;
    readonly asset_type: CreativeAssetType;
    readonly client_id: string | null;
  };
  readonly client_id: string;
  readonly range: { readonly date_from: string; readonly date_to: string };
  readonly existing_matches: ReadonlyArray<CreativeAssetExistingPaidMatch>;
  readonly suggestions: ReadonlyArray<CreativeAssetPaidMatchSuggestion>;
  readonly unmatched: ReadonlyArray<CreativeAssetUnmatchedAd>;
  readonly platforms: Readonly<
    Record<CreativeIntelligencePlatform, CreativeAssetPaidMatchPlatformConnection>
  >;
  readonly partial: boolean;
}

export interface SuggestCreativeAssetPaidMatchesParams {
  readonly assetId: string;
  readonly clientId: string;
  readonly dateFrom: string;
  readonly dateTo: string;
  readonly signal?: AbortSignal;
}

export interface CreateCreativeAssetMatchInput {
  readonly asset_id: string;
  readonly ad_id: string;
}

export interface CreateCreativeAssetMatchData {
  readonly match: CreativeAssetMatchRow;
}

// ── influencers ──────────────────────────────────────────────────────

export type InfluencerTier =
  | 'nano'
  | 'micro'
  | 'mid_tier'
  | 'macro'
  | 'mega'
  | 'celebrity'
  | 'unknown';

export type InfluencerStatus = 'active' | 'paused' | 'blocked' | 'archived';

export interface InfluencerRow {
  readonly id: string;
  readonly display_name: string;
  readonly legal_name: string | null;
  readonly public_bio: string | null;
  readonly primary_country: string | null;
  readonly primary_city: string | null;
  readonly languages: ReadonlyArray<string>;
  readonly categories: ReadonlyArray<string>;
  readonly tier: InfluencerTier;
  readonly status: InfluencerStatus;
  readonly archived_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListInfluencersData {
  readonly influencers: ReadonlyArray<InfluencerRow>;
}

export interface GetInfluencerData {
  readonly influencer: InfluencerRow;
}

export type InfluencerPlatform =
  | 'instagram'
  | 'tiktok'
  | 'snapchat'
  | 'youtube'
  | 'x'
  | 'linkedin'
  | 'other';

export interface InfluencerPlatformAccountRow {
  readonly id: string;
  readonly influencer_id: string;
  readonly platform: InfluencerPlatform;
  readonly handle: string;
  readonly profile_url: string | null;
  readonly follower_count: number | null;
  readonly average_views: number | null;
  readonly average_engagement_rate: number | null;
  readonly audience_summary: Readonly<Record<string, unknown>>;
  readonly last_refreshed_at: string | null;
  readonly archived_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListInfluencerPlatformAccountsData {
  readonly platform_accounts: ReadonlyArray<InfluencerPlatformAccountRow>;
}

// ── reports ──────────────────────────────────────────────────────────

export type ReportType =
  | 'client_summary'
  | 'initiative_progress'
  | 'content_performance'
  | 'publishing_performance'
  | 'paid_media_performance'
  | 'crm_pipeline'
  | 'operations_overview'
  | 'finance_summary'
  | 'custom';

export type ReportJobStatus = 'queued' | 'processing' | 'succeeded' | 'failed' | 'cancelled';

export interface ReportJobRow {
  readonly id: string;
  readonly organization_id: string;
  readonly client_id: string | null;
  readonly report_type: ReportType;
  readonly title: string;
  readonly locale: string;
  readonly timezone: string;
  readonly period_start: string | null;
  readonly period_end: string | null;
  readonly status: ReportJobStatus;
  readonly requested_by_user_id: string;
  readonly requested_at: string;
  readonly started_at: string | null;
  readonly completed_at: string | null;
  readonly failed_at: string | null;
  readonly cancelled_at: string | null;
  readonly failure_code: string | null;
  readonly archived_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListReportJobsData {
  readonly report_jobs: ReadonlyArray<ReportJobRow>;
}

export interface GetReportJobData {
  readonly report_job: ReportJobRow;
}

export type ReportArtifactFormat = 'html' | 'pdf' | 'csv' | 'xlsx' | 'json';

export interface ReportArtifactRow {
  readonly id: string;
  readonly report_job_id: string;
  readonly artifact_format: ReportArtifactFormat;
  readonly file_name: string | null;
  readonly content_type: string | null;
  readonly byte_size: number | null;
  readonly checksum_sha256: string | null;
  readonly archived_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListReportArtifactsData {
  readonly report_artifacts: ReadonlyArray<ReportArtifactRow>;
}

// ── crm ──────────────────────────────────────────────────────────────

export type CrmLeadStatus =
  | 'new'
  | 'contacted'
  | 'qualified'
  | 'unqualified'
  | 'converted'
  | 'archived';

export interface CrmLeadListRow {
  readonly id: string;
  readonly organization_id: string;
  readonly owner_user_id: string | null;
  readonly created_by_user_id: string | null;
  readonly company_name: string;
  readonly contact_name: string | null;
  readonly contact_email: string | null;
  readonly contact_phone: string | null;
  readonly source: string | null;
  readonly status: CrmLeadStatus;
  readonly estimated_value: number | string | null;
  readonly currency_code: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListCrmLeadsData {
  readonly leads: ReadonlyArray<CrmLeadListRow>;
}

export interface CrmLeadDetailRow extends CrmLeadListRow {
  readonly notes: string | null;
  readonly archived_at: string | null;
}

export interface GetCrmLeadData {
  readonly lead: CrmLeadDetailRow;
}

export type CrmDealStage = 'discovery' | 'proposal' | 'negotiation' | 'won' | 'lost';

export interface CrmDealListRow {
  readonly id: string;
  readonly organization_id: string;
  readonly lead_id: string | null;
  readonly owner_user_id: string | null;
  readonly created_by_user_id: string | null;
  readonly title: string;
  readonly stage: CrmDealStage;
  readonly expected_value: number | string | null;
  readonly currency_code: string | null;
  readonly probability: number | null;
  readonly expected_close_date: string | null;
  readonly won_at: string | null;
  readonly lost_at: string | null;
  readonly lost_reason: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListCrmDealsData {
  readonly deals: ReadonlyArray<CrmDealListRow>;
}

export interface CrmDealDetailRow extends CrmDealListRow {
  readonly archived_at: string | null;
}

export interface GetCrmDealData {
  readonly deal: CrmDealDetailRow;
}

export type CrmDealActivityType =
  | 'note'
  | 'call'
  | 'email'
  | 'meeting'
  | 'proposal_sent'
  | 'follow_up'
  | 'other';

export interface CrmDealActivityRow {
  readonly id: string;
  readonly deal_id: string;
  readonly user_id: string;
  readonly activity_type: CrmDealActivityType;
  readonly subject: string;
  readonly body: string | null;
  readonly occurred_at: string;
  readonly due_at: string | null;
  readonly completed_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListCrmDealActivitiesData {
  readonly deal_activities: ReadonlyArray<CrmDealActivityRow>;
}

export interface CrmDealStageHistoryRow {
  readonly id: string;
  readonly deal_id: string;
  readonly from_stage: CrmDealStage | null;
  readonly to_stage: CrmDealStage;
  readonly changed_by_user_id: string | null;
  readonly changed_at: string;
  readonly reason: string | null;
}

export interface ListCrmDealStageHistoryData {
  readonly deal_stage_history: ReadonlyArray<CrmDealStageHistoryRow>;
}

// ── publishing ───────────────────────────────────────────────────────

export type SocialConnectionPlatform = 'instagram' | 'facebook' | 'tiktok' | 'snapchat';

export type SocialConnectionStatus = 'active' | 'reauthorization_required' | 'disabled' | 'revoked';

// Task 112C — capability set granted by the OAuth scopes the user
// accepted at connect time. V1 vocabulary; future capabilities
// land via additive ALTER on the DB CHECK constraint.
export type SocialConnectionCapability = 'publishing' | 'ads' | 'replies';

export interface SocialConnectionRow {
  readonly id: string;
  readonly organization_id: string;
  readonly client_id: string;
  readonly platform: SocialConnectionPlatform;
  readonly account_external_id: string;
  readonly account_name: string;
  readonly status: SocialConnectionStatus;
  // Task 112C — exposed by `list-social-connections` after the
  // OAuth callback derives them from provider-granted scopes.
  // The FE renders one badge per capability; token / scope fields
  // remain hidden by the read endpoint.
  readonly capabilities: ReadonlyArray<SocialConnectionCapability>;
  readonly connected_by_user_id: string;
  readonly archived_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListSocialConnectionsData {
  readonly connections: ReadonlyArray<SocialConnectionRow>;
}

// Task 112C — start social-connection OAuth wire shape.
export type SocialConnectionOAuthPlatform = 'instagram' | 'tiktok';

export interface StartSocialConnectionOAuthInput {
  readonly client_id: string;
  readonly platform: SocialConnectionOAuthPlatform;
  /** Optional FE-supplied "where to send the user after OAuth completes"
   * path. The callback function URL-validates it before redirecting. */
  readonly redirect_back_to?: string | null;
}

export interface StartSocialConnectionOAuthData {
  /** Provider-side authorize URL the FE opens (popup or top-level). */
  readonly authorize_url: string;
  /** Opaque state token. The FE forwards it back to the callback
   * function alongside the provider-returned `code`. NEVER log. */
  readonly state: string;
  readonly expires_at: string;
  readonly platform: SocialConnectionOAuthPlatform;
}

// Task 112C — disconnect social-connection wire shape.
export interface DisconnectSocialConnectionInput {
  readonly connection_id: string;
  readonly reason?: string | null;
}

export interface DisconnectSocialConnectionData {
  readonly connection_id: string;
  readonly status: 'revoked';
  readonly archived_at: string;
}

// ── Task 104C — publishing workflow V1 ────────────────────────────────

/**
 * V1 publish surface — three platforms only. Future LinkedIn / X
 * platforms land in this union without enum migration.
 */
export type PublishWorkflowPlatform = 'instagram' | 'tiktok' | 'snapchat';

export const PUBLISH_WORKFLOW_PLATFORMS: ReadonlyArray<PublishWorkflowPlatform> = [
  'instagram',
  'tiktok',
  'snapchat',
];

/**
 * publish_method discriminator. V1 ships `manual` only — `automated`
 * is reserved for the future run-publish-jobs path that wraps the same
 * publish_jobs / publish_attempts model with a real platform adapter.
 */
export type PublishMethod = 'manual' | 'automated';

export type PublishJobStatus = 'scheduled' | 'processing' | 'succeeded' | 'failed' | 'cancelled';

/**
 * Task 291B — `list-publish-jobs` returns TWO shapes:
 *   - agency/staff callers: every field below (unchanged);
 *   - client-category callers: the allowlisted projection only
 *     (`id`, `status`, `scheduled_for`, `created_at`, and
 *     `publish_targets.target` + `…content_items.{id,title}`).
 *
 * The agency-only members are therefore marked OPTIONAL: they are absent — not
 * null — in a client response. Every consumer already reads them behind a
 * truthiness check, so this is a type-honesty change, not a behavior change.
 * Do NOT "tidy" the `?` away: it is what stops new client-facing code from
 * assuming an internal field is there.
 */
export interface PublishJobContentItemEmbed {
  readonly id: string;
  readonly client_id?: string;
  readonly organization_id?: string;
  readonly status?: ContentItemStatus;
  readonly title: string;
}

export interface PublishJobContentVariantEmbed {
  readonly id?: string;
  readonly platform?: ContentVariantPlatform;
  readonly status?: ContentVariantStatus;
  readonly content_item_id?: string;
  readonly content_items: PublishJobContentItemEmbed;
}

export interface PublishJobTargetEmbed {
  readonly id?: string;
  readonly target: ContentVariantPlatform;
  readonly publish_method?: PublishMethod;
  readonly archived_at?: string | null;
  readonly content_variants: PublishJobContentVariantEmbed;
}

export interface PublishJobRow {
  readonly id: string;
  readonly publish_target_id?: string;
  readonly status: PublishJobStatus;
  readonly scheduled_for: string;
  readonly idempotency_key?: string;
  readonly requested_by_user_id?: string | null;
  readonly service_locked_at?: string | null;
  readonly started_at?: string | null;
  readonly completed_at?: string | null;
  readonly failed_at?: string | null;
  readonly cancelled_at?: string | null;
  readonly failure_code?: string | null;
  readonly failure_message?: string | null;
  readonly external_post_url?: string | null;
  readonly external_post_id?: string | null;
  readonly completed_by_user_id?: string | null;
  readonly failed_by_user_id?: string | null;
  readonly cancelled_by_user_id?: string | null;
  readonly created_at: string;
  readonly updated_at?: string;
  readonly publish_targets: PublishJobTargetEmbed | null;
}

export interface ListPublishJobsData {
  readonly jobs: ReadonlyArray<PublishJobRow>;
}

export interface ScheduleContentItemPublishInput {
  readonly content_item_id: string;
  readonly scheduled_for: string;
  readonly platforms: ReadonlyArray<PublishWorkflowPlatform>;
  // Operator notes only — NOT customer-facing copy. Stored in
  // publish_target / publish_job metadata under `operator_notes`.
  // Caption overrides are intentionally NOT supported: captions are
  // immutable after `client_asset_approved`. To change wording per
  // platform, return the item to draft and re-run content approval.
  readonly notes?: string | null;
}

export interface ScheduledPublishJobSummary {
  readonly publish_job_id: string;
  readonly publish_target_id: string;
  readonly content_variant_id: string;
  readonly platform: SocialConnectionPlatform;
  readonly target: ContentVariantPlatform;
  readonly scheduled_for: string;
  readonly status: PublishJobStatus;
}

export interface ScheduleContentItemPublishData {
  readonly content_item_id: string;
  readonly scheduled_for: string;
  readonly jobs: ReadonlyArray<ScheduledPublishJobSummary>;
}

// ── Task 104D — automated publishing FE wire surface DEFERRED ─────────
//
// Auto-publish backend (`schedule-content-item-auto-publish` edge fn,
// `claim_due_publish_jobs_v1` / `complete_automated_publish_job_v1`
// RPCs, `run-publish-jobs` cron runner) is shipped server-side, BUT
// the FE wire types + endpoint wrapper are deliberately NOT added in
// this branch. Reasons:
//   1. The 105A-era pin in `ContentPage.task104C.test.ts` enforces
//      that `interface ScheduleContentItemAuto*` does NOT appear in
//      this file — that pin was the post-approval boundary contract
//      ("no auto-publish leakage into client UI without explicit
//      sign-off").
//   2. This branch is BACKEND-ONLY for the 110A boundary integration
//      (real-adapter rollout, FE auto-publish UI, OAuth scaffold are
//      separate tracks).
//   3. The wire types can be added in a follow-up FE-wiring task once
//      the 105A pin is consciously revisited and updated.
//
// Add the types + endpoint wrapper in that follow-up task. Until
// then the FE has zero auto-publish surface.

export interface MarkPublishJobPublishedInput {
  readonly publish_job_id: string;
  readonly external_post_url?: string | null;
  readonly external_post_id?: string | null;
  readonly notes?: string | null;
}

export interface MarkPublishJobPublishedData {
  readonly publish_job_id: string;
  readonly publish_attempt_id: string;
  readonly publish_log_id: string;
  readonly prev_job_status: PublishJobStatus;
  readonly new_job_status: PublishJobStatus;
  readonly content_item_id: string;
  readonly prev_item_status: ContentItemStatus;
  readonly new_item_status: ContentItemStatus;
}

export interface MarkPublishJobFailedInput {
  readonly publish_job_id: string;
  readonly reason: string;
}

export interface MarkPublishJobFailedData {
  readonly publish_job_id: string;
  readonly publish_attempt_id: string;
  readonly publish_log_id: string;
  readonly prev_job_status: PublishJobStatus;
  readonly new_job_status: PublishJobStatus;
  readonly content_item_id: string;
  readonly item_status: ContentItemStatus;
}

export interface CancelPublishJobInput {
  readonly publish_job_id: string;
  readonly reason?: string | null;
}

export interface CancelPublishJobData {
  readonly publish_job_id: string;
  readonly publish_log_id: string;
  readonly prev_job_status: PublishJobStatus;
  readonly new_job_status: PublishJobStatus;
  readonly content_item_id: string;
  readonly prev_item_status: ContentItemStatus;
  readonly new_item_status: ContentItemStatus;
}

// Task 059 — Website to Onboarding Extractor.
// The endpoint persists a draft suggestion and returns a summary;
// Task 060 added the apply endpoint, and Task 163 made the wizard
// auto-apply with `overwrite_existing: false` so AI values fill empty
// fields in place (no standalone review surface).

export interface ExtractClientOnboardingWarning {
  readonly code: string;
  readonly message: string;
}

// Task 060 + Task 163 — Apply extraction suggestions.
//
// `ClientOnboardingSuggestionStatus` is still referenced by
// `ApplyClientOnboardingSuggestionData.status` (the apply endpoint
// reports the suggestion's terminal status). The list / reject
// suggestion types were dropped in Task 163 when the standalone
// review surface was removed from the wizard; the underlying edge
// functions stay deployed for backend / audit use.

export type ClientOnboardingSuggestionStatus =
  | 'pending_review'
  | 'applied'
  | 'rejected'
  | 'superseded';

/** White-listed brand_info column names accepted by the apply endpoint. */
export type ApplyBrandInfoField =
  | 'brand_name'
  | 'industry'
  | 'website_url'
  | 'primary_locale'
  | 'notes';

/** White-listed client_onboarding_profiles column names accepted by the apply endpoint. */
export type ApplyProfileField =
  | 'arabic_description'
  | 'english_description'
  | 'vision'
  | 'mission'
  | 'values_text'
  | 'usp'
  | 'strengths'
  | 'weaknesses'
  | 'offers_notes';

export interface ApplyAcceptedFields {
  readonly brand_info?: ReadonlyArray<ApplyBrandInfoField>;
  readonly profile?: ReadonlyArray<ApplyProfileField>;
  readonly products?: ReadonlyArray<number>;
  readonly audiences?: ReadonlyArray<number>;
  readonly competitors?: ReadonlyArray<number>;
}

export interface ApplyClientOnboardingSuggestionInput {
  readonly suggestion_id: string;
  readonly accepted_fields: ApplyAcceptedFields;
  readonly overwrite_existing?: boolean;
}

/** A single skip reason returned to the caller for a non-applied field. */
export interface ApplySkipReason {
  /** `brand_info` / `profile` / `products` / `audiences` / `competitors` */
  readonly section: 'brand_info' | 'profile' | 'products' | 'audiences' | 'competitors';
  /** Field name (for brand_info / profile) or stringified index (for arrays). */
  readonly key: string;
  /** Machine-readable reason. */
  readonly code:
    | 'no_value_in_suggestion'
    | 'existing_value_protected'
    | 'duplicate_name'
    | 'duplicate_label'
    | 'invalid_index';
  /** Brief Arabic message safe for UI display. */
  readonly message: string;
}

export interface ApplyClientOnboardingSuggestionData {
  readonly suggestion_id: string;
  readonly status: ClientOnboardingSuggestionStatus;
  readonly applied_fields: {
    readonly brand_info: ReadonlyArray<ApplyBrandInfoField>;
    readonly profile: ReadonlyArray<ApplyProfileField>;
    readonly products_added: number;
    readonly competitors_added: number;
    readonly audiences_added_ids: ReadonlyArray<string>;
  };
  readonly skipped: ReadonlyArray<ApplySkipReason>;
  readonly superseded_suggestion_ids: ReadonlyArray<string>;
  readonly reviewed_at: string;
}

// Task 061 + Task 085 — AI Strategy Worker (async).

export type GenerateClientStrategyAiMode = 'new_version' | 'draft_only';

export interface GenerateClientStrategyAiInput {
  readonly client_id: string;
  readonly mode: GenerateClientStrategyAiMode;
}

export interface GenerateClientStrategyAiWarning {
  readonly code: string;
  readonly message: string;
}

/**
 * Task 085 — async start response. The POST handler returns
 * immediately (HTTP 202) with a `job_id` the frontend polls. The
 * actual generation runs in a background task.
 *
 * `status` discriminates the three start outcomes:
 *   - `started` — fresh job inserted; the background task is in flight.
 *   - `already_running` — a queued/running job already exists for
 *     this client; the response carries that job's id so the caller
 *     attaches polling to it instead of double-spending tokens.
 *   - `recently_succeeded` — a job for this client succeeded within
 *     the past minute; the caller should refresh strategies instead
 *     of starting a fresh run.
 */
export type GenerateClientStrategyAiStartStatus =
  | 'started'
  | 'already_running'
  | 'recently_succeeded';

export interface GenerateClientStrategyAiStartData {
  readonly status: GenerateClientStrategyAiStartStatus;
  readonly job_id: string;
  readonly client_id: string;
  readonly started_at: string;
  readonly estimated_seconds: number;
  readonly message: string;
}

/**
 * Task 085 — strategy generation job lifecycle:
 *   queued    — POST handler inserted the row; background not yet started.
 *   running   — background task is in flight.
 *   succeeded — version published; `strategy_version_id` is set.
 *   failed    — terminal failure; `error_code` + `error_message` are set.
 */
export type StrategyGenerationJobStatus = 'queued' | 'running' | 'succeeded' | 'failed';

export interface StrategyGenerationJobRow {
  readonly id: string;
  readonly organization_id: string;
  readonly client_id: string;
  readonly mode: GenerateClientStrategyAiMode;
  readonly status: StrategyGenerationJobStatus;
  readonly ai_run_id: string | null;
  readonly strategy_version_id: string | null;
  readonly error_code: string | null;
  readonly error_message: string | null;
  readonly created_at: string;
  readonly started_at: string | null;
  readonly finished_at: string | null;
}

export interface GetStrategyGenerationStatusData {
  readonly job: StrategyGenerationJobRow | null;
  readonly estimated_seconds: number;
}

// Task 065 — AI Strategy Section Refine.

export type StrategySectionKey =
  | 'positioning'
  | 'value_proposition'
  | 'brand_values'
  | 'target_personas'
  | 'competitor_summary'
  | 'content_pillars'
  | 'tone_guidelines'
  | 'channel_recommendations'
  | 'monthly_content_mix'
  | 'roadmap'
  | 'kpi_targets'
  | 'dos_donts';

export const STRATEGY_SECTION_KEYS: ReadonlyArray<StrategySectionKey> = [
  'positioning',
  'value_proposition',
  'brand_values',
  'target_personas',
  'competitor_summary',
  'content_pillars',
  'tone_guidelines',
  'channel_recommendations',
  'monthly_content_mix',
  'roadmap',
  'kpi_targets',
  'dos_donts',
];

export type RefineClientStrategySectionMode = 'preview' | 'publish';

export interface RefineClientStrategySectionWarning {
  readonly code: string;
  readonly message: string;
}

/**
 * Preview input — runs the AI worker once and returns a deterministic
 * payload the caller can later publish without re-running the model.
 */
export interface RefineClientStrategySectionPreviewInput {
  readonly mode: 'preview';
  readonly strategy_id: string;
  readonly section_key: StrategySectionKey;
  readonly instruction: string;
}

/**
 * Publish input — does NOT call the AI. Carries the full preview
 * payload + the base version pinning. The server re-validates the
 * `refined_section` shape, verifies the preview `ai_run_id` belongs
 * to this caller / client / capability, and rejects with 409 if the
 * active version has advanced past `base_version_id` /
 * `base_version_number`. The raw instruction is NEVER sent here —
 * only the hash, excerpt, and length the preview returned.
 */
export interface RefineClientStrategySectionPublishInput {
  readonly mode: 'publish';
  readonly strategy_id: string;
  readonly section_key: StrategySectionKey;
  readonly refined_section: unknown;
  readonly base_version_id: string;
  readonly base_version_number: number;
  readonly preview_ai_run_id: string;
  readonly preview_model: string;
  readonly preview_cost_usd: number;
  readonly preview_tokens_input: number;
  readonly preview_tokens_output: number;
  readonly preview_duration_ms: number;
  readonly preview_confidence_score: number;
  readonly preview_placeholders: ReadonlyArray<string>;
  readonly instruction_hash: string;
  readonly instruction_excerpt: string;
  readonly instruction_length: number;
  /**
   * Pass back the `refined_section_hash` returned by the preview
   * call. Server recomputes the canonical hash from the parsed
   * `refined_section` and compares it to BOTH this claim AND the
   * value bound to `ai_runs.metadata.refined_section_hash`. Any
   * mismatch is rejected before publish.
   */
  readonly refined_section_hash: string;
}

export type RefineClientStrategySectionInput =
  | RefineClientStrategySectionPreviewInput
  | RefineClientStrategySectionPublishInput;

export interface RefineClientStrategySectionPreviewData {
  readonly mode: 'preview';
  readonly strategy_id: string;
  readonly section_key: StrategySectionKey;
  /** Strict V1 shape for the targeted section. UI renders read-only. */
  readonly refined_section: unknown;
  readonly summary_delta: string | null;
  readonly warnings: ReadonlyArray<RefineClientStrategySectionWarning>;
  readonly placeholders: ReadonlyArray<string>;
  readonly confidence_score: number;
  readonly ai_run_id: string;
  readonly model: string;
  readonly cost_usd: number;
  readonly tokens_input: number;
  readonly tokens_output: number;
  readonly duration_ms: number;
  readonly base_version_id: string;
  readonly base_version_number: number;
  /** SHA-256 hex of the raw instruction. Pass back to publish unchanged. */
  readonly instruction_hash: string;
  readonly instruction_excerpt: string;
  readonly instruction_length: number;
  /**
   * SHA-256 hex of the canonical-JSON serialization of
   * `refined_section`. Server-bound on `ai_runs.metadata` during
   * preview. Pass back to publish unchanged; the publish path
   * recomputes the hash from the parsed `refined_section` and
   * refuses to publish unless body claim, recomputed value, and
   * bound value all match.
   */
  readonly refined_section_hash: string;
}

export interface RefineClientStrategySectionPublishData {
  readonly mode: 'publish';
  readonly strategy_id: string;
  readonly section_key: StrategySectionKey;
  readonly refined_section: unknown;
  /** Server-recomputed hash of the published refined_section. */
  readonly refined_section_hash: string;
  readonly placeholders: ReadonlyArray<string>;
  readonly confidence_score: number;
  /** Echoed from the preview run; no fresh AI call happens on publish. */
  readonly ai_run_id: string;
  readonly model: string;
  readonly cost_usd: number;
  readonly tokens_input: number;
  readonly tokens_output: number;
  readonly duration_ms: number;
  readonly base_version_id: string;
  readonly base_version_number: number;
  readonly version_id: string;
  readonly version_number: number;
}

export type RefineClientStrategySectionData =
  | RefineClientStrategySectionPreviewData
  | RefineClientStrategySectionPublishData;

/**
 * Legacy synchronous extract response shape. The master EF source
 * still emits this directly from POST (used in local dev). The
 * deployed prod EF (Task 093) emits the async start shape below;
 * the wizard branches on the response at runtime.
 */
export interface ExtractClientOnboardingFromWebsiteData {
  readonly suggestion_id: string;
  readonly ai_run_id: string | null;
  readonly status: 'pending_review' | 'applied' | 'rejected' | 'superseded';
  readonly source_url: string;
  readonly fetched_pages_count: number;
  readonly confidence_score: number | null;
  readonly warnings: ReadonlyArray<ExtractClientOnboardingWarning>;
  readonly summary: string;
  readonly products_count: number;
  readonly audiences_count: number;
  readonly competitors_count: number;
  /** True when an existing pending suggestion with the same content fingerprint was returned without re-running the AI. */
  readonly deduplicated: boolean;
  readonly model: string;
  readonly cost_usd: number;
  readonly tokens_input: number;
  readonly tokens_output: number;
  readonly duration_ms: number;
}

// ---------------------------------------------------------------------
// Task 165 — async extraction contract.
//
// Mirrors the deployed prod EF (Task 093 internal) which dispatches
// extraction to a background job and returns 202 with a `job_id`. The
// frontend polls the GET handler until terminal. The legacy
// synchronous EF (still in master `supabase/functions/`) keeps emitting
// `ExtractClientOnboardingFromWebsiteData` directly; the wizard
// branches on the response shape so both work.
//
// Drift note: the deployed EF source / `client_onboarding_extraction_jobs`
// migration is NOT in master. Reconciliation is a follow-up task.
// ---------------------------------------------------------------------

export type ExtractClientOnboardingFromWebsiteStartStatus =
  | 'started'
  | 'already_running'
  | 'recently_succeeded';

export interface ExtractClientOnboardingFromWebsiteStartData {
  readonly status: ExtractClientOnboardingFromWebsiteStartStatus;
  readonly job_id: string;
  readonly client_id: string;
  readonly source_url: string;
  readonly started_at: string;
  readonly estimated_seconds: number;
  readonly message: string;
  /**
   * Populated only for `recently_succeeded` (dedup short-circuit on
   * a recently terminal job). For `started` / `already_running` the
   * client polls the GET handler to get the suggestion_id once the
   * job is terminal.
   */
  readonly suggestion_id?: string | null;
}

/**
 * Discriminated union the wizard sees on POST. The legacy sync EF
 * still emits `ExtractClientOnboardingFromWebsiteData`; the async
 * deployed EF emits the start data. The wizard branches at runtime
 * on `'status'` field presence + value.
 */
export type ExtractClientOnboardingFromWebsiteResponse =
  | ExtractClientOnboardingFromWebsiteStartData
  | ExtractClientOnboardingFromWebsiteData;

export type OnboardingExtractionJobStatus = 'queued' | 'running' | 'succeeded' | 'failed';

export interface OnboardingExtractionJobRow {
  readonly id: string;
  readonly client_id: string;
  readonly status: OnboardingExtractionJobStatus;
  readonly source_url: string;
  readonly suggestion_id: string | null;
  readonly ai_run_id: string | null;
  readonly error_code: string | null;
  readonly error_message: string | null;
  readonly created_at: string;
  readonly started_at: string | null;
  readonly finished_at: string | null;
  /**
   * May carry `products_count` / `audiences_count` / `competitors_count`
   * the wizard can use to dimension the apply call's accepted-fields
   * indices. Absent or missing keys are handled by the wizard via
   * safe worker-cap fallbacks (MAX_PRODUCTS=20 / MAX_AUDIENCES=10 /
   * MAX_COMPETITORS=10); the apply EF skips out-of-range indices as
   * `invalid_index`, so over-sending is harmless.
   */
  readonly metadata?: Record<string, unknown> | null;
}

export interface GetClientOnboardingExtractionStatusData {
  readonly job: OnboardingExtractionJobRow | null;
  readonly estimated_seconds?: number;
}

// ---------------------------------------------------------------------
// Task 071 — AI Prompt Blocks (Knowledge Library)
// ---------------------------------------------------------------------

export type AiPromptBlockScope = 'agency' | 'client' | 'industry' | 'platform' | 'service' | 'task';

export type AiPromptBlockStatus = 'draft' | 'approved' | 'archived';

export interface AiPromptBlockRow {
  readonly id: string;
  readonly organization_id: string;
  readonly client_id: string | null;
  readonly scope: AiPromptBlockScope;
  readonly status: AiPromptBlockStatus;
  readonly title: string;
  readonly body: string;
  readonly examples: ReadonlyArray<unknown>;
  readonly applies_to: ReadonlyArray<string>;
  readonly platforms: ReadonlyArray<ContentVariantPlatform>;
  readonly industry: string | null;
  readonly service_key: string | null;
  readonly priority: number;
  readonly version: number;
  readonly created_by_user_id: string;
  readonly updated_by_user_id: string;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListAiPromptBlocksParams {
  readonly status?: AiPromptBlockStatus;
  readonly scope?: AiPromptBlockScope;
  readonly client_id?: string;
  readonly platform?: ContentVariantPlatform;
  readonly industry?: string;
  readonly service_key?: string;
  readonly applies_to?: string;
  readonly limit?: number;
}

export interface ListAiPromptBlocksData {
  readonly ai_prompt_blocks: ReadonlyArray<AiPromptBlockRow>;
}

export interface CreateAiPromptBlockInput {
  readonly scope: AiPromptBlockScope;
  readonly client_id: string | null;
  readonly title: string;
  readonly body: string;
  readonly examples?: ReadonlyArray<unknown>;
  readonly applies_to?: ReadonlyArray<string>;
  readonly platforms?: ReadonlyArray<ContentVariantPlatform>;
  readonly industry?: string | null;
  readonly service_key?: string | null;
  readonly priority?: number;
}

export interface CreateAiPromptBlockData {
  readonly ai_prompt_block: AiPromptBlockRow;
}

export interface UpdateAiPromptBlockInput {
  readonly id: string;
  readonly title?: string;
  readonly body?: string;
  readonly examples?: ReadonlyArray<unknown>;
  readonly applies_to?: ReadonlyArray<string>;
  readonly platforms?: ReadonlyArray<ContentVariantPlatform>;
  readonly industry?: string | null;
  readonly service_key?: string | null;
  readonly priority?: number;
  readonly scope?: AiPromptBlockScope;
  readonly client_id?: string | null;
}

export interface UpdateAiPromptBlockData {
  readonly ai_prompt_block: AiPromptBlockRow;
}

export interface TransitionAiPromptBlockStatusInput {
  readonly id: string;
  readonly target_status: AiPromptBlockStatus;
}

export interface TransitionAiPromptBlockStatusData {
  readonly id: string;
  readonly prev_status: AiPromptBlockStatus;
  readonly new_status: AiPromptBlockStatus;
  readonly new_version: number;
}

// ---------------------------------------------------------------------
// Task 073 — Creative asset upload + attach (preview-only on the FE).
// ---------------------------------------------------------------------

export type CreativeAssetTargetSource = 'content_item' | 'content_variant';
export type CreativeAssetUploadKind = 'image' | 'video';

export interface CreateCreativeAssetUploadInput {
  readonly client_id: string;
  readonly target_source: CreativeAssetTargetSource;
  readonly target_id: string;
  readonly asset_type: CreativeAssetUploadKind;
  readonly mime_type: string;
  readonly byte_size: number;
  readonly filename: string;
}

export interface CreateCreativeAssetUploadData {
  readonly upload_intent_id: string;
  readonly upload_url: string;
  readonly upload_url_expires_at: string;
  readonly required_headers: Readonly<Record<string, string>>;
  readonly intent_expires_at: string;
  readonly bucket: string;
}

export interface CompleteCreativeAssetUploadInput {
  readonly upload_intent_id: string;
  readonly canonical_name?: string | null;
  readonly checksum_sha256?: string | null;
  readonly width_px?: number | null;
  readonly height_px?: number | null;
  readonly duration_ms?: number | null;
}

export interface CreativeAssetCardRow {
  readonly id: string;
  readonly target_source: CreativeAssetTargetSource;
  readonly target_id: string;
  readonly asset_type: string;
  readonly canonical_name: string;
  readonly mime_type: string | null;
  readonly byte_size: number | null;
  readonly width: number | null;
  readonly height: number | null;
  readonly duration_ms: number | null;
  readonly created_at: string;
  readonly signed_url: string;
  readonly signed_url_expires_at: string;
}

export interface CompleteCreativeAssetUploadData {
  readonly creative_asset: {
    readonly id: string;
    readonly organization_id?: string;
    readonly client_id?: string | null;
    readonly asset_type?: string;
    readonly canonical_name?: string;
    readonly mime_type?: string | null;
    readonly byte_size?: number | null;
    readonly width?: number | null;
    readonly height?: number | null;
    readonly duration_ms?: number | null;
    readonly created_at?: string;
  };
  readonly target_source: CreativeAssetTargetSource;
  readonly target_id: string;
  readonly signed_url: string;
  readonly signed_url_expires_at: string;
}

// ---------------------------------------------------------------------
// Task 236 / 237 — Creative Intelligence standalone BULK upload (no content
// target, no match). Mirrors the content upload types but ARRAY-shaped.
// ---------------------------------------------------------------------

export type CiAssetUploadKind = 'image' | 'video';
export type CiCreatePerFileStatus = 'ready_to_upload' | 'unsupported_type' | 'failed';
export type CiCompletePerItemStatus = 'uploaded' | 'duplicate' | 'failed';

export interface CreateCiAssetUploadsFile {
  readonly filename: string;
  readonly asset_type: CiAssetUploadKind;
  readonly mime_type: string;
  readonly byte_size: number;
}

export interface CreateCiAssetUploadsInput {
  readonly client_id: string;
  readonly files: ReadonlyArray<CreateCiAssetUploadsFile>;
}

export interface CreateCiAssetUploadResult {
  readonly filename: string;
  readonly status: CiCreatePerFileStatus;
  readonly upload_intent_id?: string;
  readonly upload_url?: string;
  readonly upload_url_expires_at?: string;
  readonly required_headers?: Readonly<Record<string, string>>;
  readonly intent_expires_at?: string;
  readonly normalized_filename?: string;
  readonly error?: string;
}

export interface CreateCiAssetUploadsData {
  readonly client_id: string;
  readonly bucket: string;
  readonly results: ReadonlyArray<CreateCiAssetUploadResult>;
}

export interface CompleteCiAssetUploadsItem {
  readonly upload_intent_id: string;
  readonly canonical_name?: string | null;
  readonly checksum_sha256?: string | null;
  readonly width_px?: number | null;
  readonly height_px?: number | null;
  readonly duration_ms?: number | null;
}

export interface CompleteCiAssetUploadsInput {
  readonly items: ReadonlyArray<CompleteCiAssetUploadsItem>;
}

export interface CompleteCiAssetUploadResult {
  readonly upload_intent_id: string;
  readonly status: CiCompletePerItemStatus;
  readonly was_duplicate?: boolean;
  readonly creative_asset?: { readonly id: string };
  readonly signed_url?: string;
  readonly signed_url_expires_at?: string;
  readonly error?: string;
}

export interface CompleteCiAssetUploadsData {
  readonly results: ReadonlyArray<CompleteCiAssetUploadResult>;
}

export interface ListContentCreativeAssetsParams {
  readonly client_id: string;
  readonly content_item_ids?: ReadonlyArray<string>;
  readonly content_variant_ids?: ReadonlyArray<string>;
}

export interface ListContentCreativeAssetsData {
  readonly assets: ReadonlyArray<CreativeAssetCardRow>;
}

// Task 076 — archive (remove) an attached creative asset.
// Server returns safe IDs / booleans only; never the storage_key.
export interface ArchiveContentCreativeAssetInput {
  readonly target_source: CreativeAssetTargetSource;
  readonly target_id: string;
  readonly creative_asset_id: string;
  readonly reason?: string | null;
}

export interface ArchiveContentCreativeAssetData {
  readonly asset_id: string;
  readonly match_id: string;
  readonly asset_archived: boolean;
  readonly match_archived: boolean;
  readonly target_source: CreativeAssetTargetSource;
  readonly target_id: string;
}

// ── Task 139 / Task 141 — IC-3 paid-media OAuth on integration-credential layer ──
//
// Mirrors the IC-3 EF wire contracts (start-/complete-integration-
// connection-oauth). Server-side authority: IC-1 + IC-2 schema (Tasks
// 136-138) + IC-3 EFs (Task 139). FE wiring: Task 141.
//
// Provider whitelist: `'meta'` (IC-3) + `'snapchat'` (IC-6 / Task 190) +
// `'tiktok'` (IC-8 / Task 204). The integration_provider enum also has
// google, but only these three ship a wired OAuth EF arm so far.

export type IntegrationConnectionOAuthProvider = 'meta' | 'snapchat' | 'tiktok';

export interface StartIntegrationConnectionOAuthInput {
  readonly client_id: string;
  readonly provider: IntegrationConnectionOAuthProvider;
  /** Optional FE-supplied "where to send the user after OAuth completes"
   * path. The IC-3 start EF persists it on the state row; the callback
   * EF echoes it back in the success body so the FE can navigate to it. */
  readonly redirect_back_to?: string | null;
}

export interface StartIntegrationConnectionOAuthData {
  /** Provider-side authorize URL the FE opens (top-level navigation). */
  readonly authorize_url: string;
  /** Opaque CSRF state token. NEVER log; NEVER persist outside of the
   * server-side `integration_oauth_states` row. The FE just forwards
   * it back via the redirect URL — Meta echoes it as `?state=`. */
  readonly state: string;
  readonly expires_at: string;
  readonly provider: IntegrationConnectionOAuthProvider;
  readonly client_id: string;
}

/**
 * First-call body for the IC-3 callback EF. The FE callback page
 * extracts `state` + `code` from the URL and POSTs this shape.
 */
export interface CompleteIntegrationConnectionOAuthFirstCallInput {
  readonly state: string;
  readonly code: string;
}

/**
 * Selection-call body for the IC-3 callback EF. Used after the user
 * picks one ad-account candidate from the multi-subject picker.
 */
export interface CompleteIntegrationConnectionOAuthSelectInput {
  readonly state: string;
  readonly selected_subject_id: string;
}

/**
 * Wire-safe row shape returned in the IC-3 callback success body.
 * Token / ciphertext / key-id / scope-internal / metadata columns
 * stay service-role-only and are NEVER on this surface (per IC-1
 * Decision 5 + Security boundaries).
 */
export interface IntegrationConnectionPublicRow {
  readonly id: string;
  readonly organization_id: string;
  readonly client_id: string;
  readonly provider: IntegrationConnectionOAuthProvider;
  readonly provider_subject_id: string;
  readonly display_name: string | null;
  readonly status: 'active';
  readonly expires_at: string | null;
  readonly redirect_back_to: string | null;
}

export interface CompleteIntegrationConnectionOAuthData {
  readonly connection: IntegrationConnectionPublicRow;
}

/**
 * Canonical shape of one ad-account candidate in the picker. The IC-3
 * callback EF returns these as a JSON-stringified array under
 * `fields.candidates[0]` when the multi-subject picker fires. NO
 * token, NO bearer, NO state.
 *
 * `subject_id` is load-bearing — the FE selection re-POST sends it as
 * `selected_subject_id`, and the EF's `finalize_integration_oauth_
 * pending_selection_v1` RPC matches against `candidates[*].subject_id`.
 */
export interface IntegrationAdAccountCandidate {
  readonly subject_id: string;
  readonly account_external_id: string;
  readonly display_name: string;
  readonly account_status: number | null;
  readonly currency: string | null;
  readonly timezone_name: string | null;
  readonly business: { readonly id: string; readonly name: string } | null;
}

// ─────────────────────────────────────────────────────────────────────
// Task 289A — monthly client report (`get-client-monthly-report`).
// ─────────────────────────────────────────────────────────────────────

export type MonthlyReportBucket =
  | 'planned'
  | 'in_production'
  | 'awaiting_client'
  | 'approved_ready'
  | 'published'
  | 'cancelled';

export interface MonthlyReportCountRow {
  readonly key: string;
  readonly count: number;
}

export interface MonthlyReportRow {
  readonly content_item_id: string;
  readonly title: string;
  readonly discipline: string;
  readonly platform: string;
  readonly item_status: string;
  readonly bucket: MonthlyReportBucket;
  readonly asset_format: string | null;
  readonly scheduled_for: string | null;
  readonly published_at: string | null;
}

export interface ClientMonthlyReportTotals {
  readonly items: number;
  readonly placements: number;
  readonly published_placements: number;
  readonly calendar_truncated: boolean;
  readonly publish_truncated: boolean;
}

/**
 * Buckets are counts of DISTINCT content items by CURRENT status.
 * Agency-only buckets (`planned`, `in_production`, `cancelled`) are
 * `null` for client-category viewers — RLS hides their member
 * statuses from clients, so a number cannot be computed honestly.
 */
export interface ClientMonthlyReportData {
  readonly client_id: string;
  readonly month: string;
  readonly timezone: string;
  readonly range: { readonly start_utc: string; readonly end_utc: string };
  readonly viewer_is_agency: boolean;
  readonly buckets: Readonly<Record<MonthlyReportBucket, number | null>>;
  readonly by_platform: ReadonlyArray<MonthlyReportCountRow>;
  readonly by_discipline: ReadonlyArray<MonthlyReportCountRow>;
  readonly by_asset_format: ReadonlyArray<MonthlyReportCountRow>;
  readonly rows: ReadonlyArray<MonthlyReportRow>;
  readonly totals: ClientMonthlyReportTotals;
}

// ── Task 309A — organic content performance (get-client-organic-performance). ──
//
// Read from `organic_performance_snapshots`, the pre-computed aggregate. The
// three rules the UI cannot reconstruct on its own all travel on the wire:
//
//   1. `status: 'unavailable'` is a real outcome. "Not computed yet" is not "a
//      period of zeros" and must not render the same way.
//   2. Every total may be `null`. `completeness.supported_post_metrics` —
//      and `supported_account_metrics` for the audience figures — says
//      whether that means «غير مدعوم» (the platform cannot report it) or «لا
//      توجد بيانات» (it can, and nothing has arrived for the period).
//      A PARTIAL period is neither, and this is where the contract changed:
//      the sum of what WAS reported is published, and
//      `completeness.metric_coverage` says how much of the population it
//      rests on, so the reader is shown «مبني على N من M» rather than an
//      absence. A partial sum is therefore never withheld — but it is also
//      never presented as a complete total. Nothing coerces to 0.
//   3. `temporality.post_metrics` decides the LABEL. When it is `cumulative`
//      the totals are lifetime figures for the posts PUBLISHED in the window,
//      not the window's own earnings. "Published in", not "seen in": a post
//      merely re-read during the window contributes nothing, which is the
//      whole point of the cohort rule.

export type OrganicPeriodKindWire = 'last_7_days' | 'last_30_days' | 'last_90_days';

export type OrganicPlatformWire = 'instagram' | 'facebook' | 'tiktok' | 'snapchat';

export type OrganicAggregateTemporalityWire =
  | 'interval'
  | 'cumulative'
  | 'mixed'
  | 'not_applicable';

export type OrganicCumulativeAggregationWire = 'latest' | 'delta' | 'not_applicable';

export type OrganicEngagementRateBasisWire = 'reach' | 'impressions' | 'followers' | 'unavailable';

export type OrganicEngagementsSourceWire = 'provider_total' | 'derived_components' | 'unavailable';

export interface OrganicPerformanceTotals {
  readonly impressions: number | null;
  readonly reach: number | null;
  readonly engagements: number | null;
  readonly reactions: number | null;
  readonly comments: number | null;
  readonly shares: number | null;
  readonly saves: number | null;
  readonly video_views: number | null;
  readonly video_watch_seconds: number | null;
  readonly clicks: number | null;
}

export interface OrganicBreakdownEntryWire {
  readonly key: string;
  readonly posts: number;
  readonly impressions: number | null;
  readonly reach: number | null;
  readonly engagements: number | null;
}

export interface OrganicRankedPostWire {
  readonly organic_post_id: string;
  readonly content_item_id: string | null;
  readonly content_variant_id: string | null;
  readonly post_format: string | null;
  readonly published_at: string | null;
  readonly permalink: string | null;
  readonly impressions: number | null;
  readonly reach: number | null;
  readonly engagements: number | null;
  readonly engagement_rate: number | string | null;
}

export interface OrganicSeriesPointWire {
  readonly date: string;
  readonly posts_with_data: number;
  readonly impressions: number | null;
  readonly reach: number | null;
  readonly engagements: number | null;
}

export interface OrganicMetricCoverageWire {
  readonly post_population?: number;
  readonly account_population?: number;
  readonly post?: Readonly<Record<string, number>>;
  readonly account?: Readonly<Record<string, number>>;
}

export interface OrganicPerformanceSnapshotWire {
  readonly id: string;
  readonly platform: OrganicPlatformWire | null;
  readonly period: {
    readonly kind: OrganicPeriodKindWire;
    readonly start: string;
    readonly end: string;
    readonly expected_days: number;
  };
  readonly source: {
    readonly account_count: number;
    readonly contains_synthetic: boolean;
  };
  readonly freshness: {
    readonly computed_at: string;
    /** Last date in the window carrying any measurement. Null = none did. */
    readonly data_through_date: string | null;
    /** "We synced" — which is not the same as "we have data through". */
    readonly last_successful_sync_at: string | null;
  };
  readonly completeness: {
    readonly expected_days: number;
    readonly days_with_data: number;
    readonly posts_in_period: number;
    readonly posts_with_metrics: number;
    /**
     * Cumulative posts dropped for having no publication date, so they could
     * not be placed in the period cohort. A different gap from
     * `posts_with_metrics < posts_in_period`.
     */
    readonly posts_excluded_undated: number;
    readonly supported_post_metrics: ReadonlyArray<string>;
    readonly supported_account_metrics: ReadonlyArray<string>;
    /**
     * Per metric, how many of the population contributed a value.
     *
     * This is the whole of the completeness contract. Earlier versions nulled
     * a total whenever its coverage was partial, through four interacting
     * rules that each had to be right against all the others; fixing one kept
     * breaking another. A sum is now a sum, and its coverage travels with it:
     * nothing is invented, nothing is silently partial, and «لا توجد بيانات»
     * keeps its narrow meaning of "nothing was reported at all".
     *
     * Coverage is per ENTITY — posts, accounts. A gap in the DAYS within a
     * post is visible through `days_with_data` against `expected_days`, not
     * here; adding a second axis is the kind of interacting rule this replaced.
     */
    readonly metric_coverage: OrganicMetricCoverageWire;
  };
  readonly temporality: {
    readonly post_metrics: OrganicAggregateTemporalityWire;
    readonly account_metrics: OrganicAggregateTemporalityWire;
    readonly cumulative_aggregation: OrganicCumulativeAggregationWire;
  };
  readonly totals: OrganicPerformanceTotals;
  readonly account_totals: {
    readonly impressions: number | null;
    readonly reach: number | null;
    readonly profile_views: number | null;
  };
  readonly audience: {
    readonly followers_start: number | null;
    readonly followers_end: number | null;
    readonly followers_delta: number | null;
    readonly new_followers: number | null;
  };
  readonly engagement_rate: number | null;
  readonly engagement_rate_basis: OrganicEngagementRateBasisWire;
  readonly engagements_source: OrganicEngagementsSourceWire;
  readonly breakdowns: {
    readonly by_platform?: ReadonlyArray<OrganicBreakdownEntryWire>;
    readonly by_format?: ReadonlyArray<OrganicBreakdownEntryWire>;
  };
  readonly top_posts: ReadonlyArray<OrganicRankedPostWire>;
  readonly bottom_posts: ReadonlyArray<OrganicRankedPostWire>;
  readonly daily_series: ReadonlyArray<OrganicSeriesPointWire>;
}

export interface ClientOrganicPerformanceData {
  readonly client_id: string;
  readonly period_kind: OrganicPeriodKindWire;
  readonly platform: OrganicPlatformWire | null;
  readonly status: 'ready' | 'unavailable';
  /**
   * `snapshot_stale` — a row exists but was computed for a window that has
   * since passed. Only the ingestion refresh recomputes a rolling row, so a
   * client whose last organic account was archived keeps its old rows for
   * ever; serving those as `ready` would answer a question about this week
   * with last month's numbers.
   */
  readonly reason: 'no_snapshot' | 'snapshot_stale' | 'platform_not_covered' | null;
  readonly available_platforms: ReadonlyArray<OrganicPlatformWire>;
  readonly snapshot: OrganicPerformanceSnapshotWire | null;
}

// ─────────────────────────────────────────────────────────────────────
// Task 298 (Foundation F) — organization commercial standing
//
// Appended as a single contiguous block per the shared-file protocol in
// `tasks/295A-commercial-foundation-and-staging.md` §9.3. Never
// interleave another lane's additions into this block; never reorder it.
//
// Commercial standing is a SEPARATE AXIS from the SaaS subscription
// (295A D2a): entitlement answers "what does this plan include",
// standing answers "may this tenant OPERATE at all". Nothing here is
// added to `MeContext` — 295A §6.1.1 is explicit that `me/context` is
// NOT widened by this task: no key is added and no `commercial_state`
// field is added. The pending route reads its standing from the
// dedicated activation-status boundary instead.
// ─────────────────────────────────────────────────────────────────────

/** The four commercial standings (295A §5.4). `terminated` is absorbing. */
export type CommercialState = 'pending_activation' | 'activated' | 'suspended' | 'terminated';

export const COMMERCIAL_STATES: ReadonlyArray<CommercialState> = [
  'pending_activation',
  'activated',
  'suspended',
  'terminated',
];

/** The six operator actions (295A §6.3). */
export type CommercialAction =
  | 'activate'
  | 'suspend'
  | 'reinstate'
  | 'terminate'
  | 'mark_paid'
  | 'mark_unpaid';

/**
 * BOUNDARY 1 payload — `get-organization-activation-status`.
 *
 * Deliberately two fields and nothing else: no `payment_status`, no
 * `notes`, no `activated_by_user_id`. The endpoint answers in all four
 * states for an AGENCY actor and fails closed for every client-category
 * actor in every state (295A §5.4, §6.1.1).
 */
export interface OrganizationActivationStatusData {
  readonly commercial_state: CommercialState;
  readonly activated_at: string | null;
}

/**
 * One row of the operator list — `list-organization-commercial-state`.
 *
 * Task 307A widened it to everything the console's table renders, all of
 * it resolved by the v2 RPC in ONE bounded query. The point of the
 * widening is that there is no per-row detail read behind the list: an
 * N+1 over the operator's own screen is the thing the design brief
 * forbids outright.
 *
 * `client_count` is an AGGREGATE and it is the only client-derived fact
 * here. No client id, name or status crosses this boundary — the
 * operator control plane governs standing, not tenant data.
 */
export interface OrganizationCommercialStateRow {
  readonly organization_id: string;
  readonly organization_name: string;
  readonly organization_slug: string;
  readonly legal_name: string | null;
  readonly city: string | null;
  readonly commercial_registration_no: string | null;
  readonly commercial_state: CommercialState;
  readonly payment_status: string;
  readonly plan_key: string | null;
  readonly plan_name_ar: string | null;
  readonly plan_name_en: string | null;
  readonly subscription_state: string | null;
  /** Over the SIX Task 299 REQUIRED fields, never over the eight. */
  readonly profile_filled: number;
  readonly profile_total: number;
  /** The server's own trigger-written verdict, reported separately. */
  readonly profile_completed_at: string | null;
  readonly client_count: number;
  readonly last_decision_action: string | null;
  readonly last_decision_at: string | null;
  readonly activated_at: string | null;
  readonly created_at: string;
}

/**
 * The assignable catalogue, carried on the LIST response — Task 307A.
 *
 * The plan filter's options have to be the plans that EXIST, not the ones
 * that happen to appear on the page being filtered. It is one bounded
 * read of a three-row table, issued in parallel with the list.
 */
export interface CommercialListPlan {
  readonly key: string;
  readonly name_ar: string | null;
  readonly name_en: string | null;
}

export interface ListOrganizationCommercialStateData {
  readonly organizations: ReadonlyArray<OrganizationCommercialStateRow>;
  readonly total: number;
  readonly plans: ReadonlyArray<CommercialListPlan>;
  /**
   * Organizations awaiting activation across ALL of them, ignoring every
   * filter. NULL when the page came back empty — the count rides on the
   * rows, so a filter that matched nothing carries no answer, and the
   * console HIDES the figure rather than printing a zero it cannot stand
   * behind.
   */
  readonly pending_total: number | null;
}

/**
 * The three list sorts — Task 307A. Mirrors the Edge Function's own
 * allow-list, and every one of them is deterministic in the RPC: each
 * ends on `organization_id`, so a page boundary can neither duplicate
 * nor skip a row.
 */
export const COMMERCIAL_LIST_SORTS = ['recent', 'least_complete', 'name'] as const;

export type CommercialListSort = (typeof COMMERCIAL_LIST_SORTS)[number];

export const DEFAULT_COMMERCIAL_LIST_SORT: CommercialListSort = 'recent';

/** The full standing row returned by a successful operator transition. */
export interface OrganizationCommercialStateDetail {
  readonly organization_id: string;
  readonly commercial_state: CommercialState;
  readonly payment_status: string;
  readonly activated_at: string | null;
  readonly activated_by_user_id: string | null;
  readonly suspended_at: string | null;
  readonly terminated_at: string | null;
  readonly notes: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface SetOrganizationCommercialStateData {
  readonly commercial_state: OrganizationCommercialStateDetail;
}

// ─────────────────────────────────────────────────────────────────────
// Task 299 (Lane O) — agency onboarding profile boundary
//
// This is intentionally separate from MeContext and from subscription /
// payment data. It remains reachable while pending_activation through its
// dedicated direct-role boundary.
// ─────────────────────────────────────────────────────────────────────

export interface AgencyProfile {
  readonly legal_name: string | null;
  readonly country_code: string | null;
  readonly city: string | null;
  readonly phone_e164: string | null;
  readonly commercial_registration_no: string | null;
  readonly vat_no: string | null;
  readonly billing_contact_name: string | null;
  readonly billing_contact_email: string | null;
  readonly completed_at: string | null;
}

export interface GetAgencyProfileData {
  readonly organization: {
    readonly id: string;
    readonly name: string;
    readonly slug: string;
  };
  readonly profile: AgencyProfile | null;
  readonly can_edit: boolean;
}

export type UpsertAgencyProfileInput = Partial<
  Record<
    | 'legal_name'
    | 'country_code'
    | 'city'
    | 'phone_e164'
    | 'commercial_registration_no'
    | 'vat_no'
    | 'billing_contact_name'
    | 'billing_contact_email',
    string | null
  >
>;

export interface UpsertAgencyProfileData {
  readonly profile: AgencyProfile;
}

// ─────────────────────────────────────────────────────────────────────
// Task 301 (Lane B) — agency SaaS subscription commercial summary
//
// Appended as a single contiguous block per the shared-file protocol in
// `tasks/295A-commercial-foundation-and-staging.md` §9.3. Never
// interleave another lane's additions into this block; never reorder it.
//
// THIS IS THE AGENCY'S OWN SUBSCRIPTION TO SUPER AGENCY — not the
// agency's service packages sold to its clients (`agency_packages`,
// surfaced at `/app/services`), not the agency-client contract, and not
// paid media. 295A §11.B1 / CLP-1.7 record that these concepts share
// vocabulary and are routinely blurred.
//
// Nothing here is added to `MeContext`: 295A §6.4 keeps `me/context`
// un-widened — no subscription state, no plan id, no limits. This
// dedicated endpoint is the only place they surface.
//
// NO PAYMENT TYPE EXISTS HERE BY DESIGN. Launch is manual activation and
// bank transfer (295A D1 / CLP-1.1, CLP-1.2); there is no checkout, no
// provider, no invoice and no payment feature flag anywhere in the
// programme (295A §14).
// ─────────────────────────────────────────────────────────────────────

/** The five SaaS subscription states (`20260423060000:63-65`). Frozen. */
export type SubscriptionStateWire = 'trialing' | 'active' | 'past_due' | 'suspended' | 'cancelled';

/**
 * Commercial bookkeeping only (`organization_commercial_state`). NO GATE
 * CONSULTS IT — Task 298 is explicit that `app.org_is_operational()` does
 * not read `payment_status`, because gating on it would turn an
 * unrecorded bank transfer into an outage.
 */
export type PaymentStatusWire = 'unpaid' | 'paid';

export interface AgencyCommercialSummaryData {
  readonly plan: {
    readonly key: string;
    readonly name_en: string;
    readonly name_ar: string;
  };
  readonly subscription: {
    readonly state: SubscriptionStateWire;
    readonly started_at: string;
    readonly current_period_ends_at: string | null;
  };
  readonly commercial: {
    readonly commercial_state: CommercialState;
    readonly payment_status: PaymentStatusWire;
    readonly activated_at: string | null;
  };
  readonly limits: {
    /**
     * `null` MEANS UNLIMITED and MUST render as unlimited, never as `0`
     * (295A §6.4). It is the `agency_v1_default` grandfather branch
     * (`_shared/entitlements.ts:552-555`) surfacing, and the production
     * tenant runs on exactly that plan.
     */
    readonly max_clients: number | null;
    /** `null` MEANS UNLIMITED. Same rule as `max_clients`. */
    readonly max_seats: number | null;
  };
  /**
   * Task 301B — CURRENT usage, counted exactly the way ENFORCEMENT
   * counts it: non-archived clients (`create-client/index.ts:242-246`)
   * and active `user_roles` rows (`assign-user-role/index.ts:210-215`).
   *
   * NEVER DERIVED FROM THE CALLER'S OWN RLS. `finance_manager` holds
   * `billing.read` but is not admitted by `clients_select`, so a
   * caller-scoped count would show it `0` clients next to a cap of 15.
   * The server reads both numbers through the no-argument definer RPC
   * `public.get_own_agency_usage_counts()`.
   */
  readonly usage: {
    readonly clients: number;
    readonly seats: number;
  };
  /**
   * Task 301B — the EFFECTIVE capability set: active per-organization
   * `feature_entitlements` layered over the current plan's
   * `plan_features`, and every entry `false` when the subscription state
   * is not entitled.
   *
   * `id` IS PRODUCT VOCABULARY, NOT A FEATURE KEY. The technical keys
   * (`publishing.enabled`, …) never cross this wire and are never
   * rendered.
   */
  readonly features: ReadonlyArray<{
    readonly id: ProductFeatureId;
    readonly included: boolean;
  }>;
  /**
   * Task 301B — the active PUBLIC plan catalogue, read dynamically from
   * `plans` / `plan_features`. After Task 301's migration that is
   * `{starter_v1, growth_v1, custom_v1}`, but no plan key, name, cap or
   * feature value is hardcoded anywhere in the client or the server.
   */
  readonly catalogue: ReadonlyArray<PlanCatalogueEntry>;
}

/**
 * Task 301B — the six product-visible capabilities the billing surface
 * names, in display order. Mirrors `PRODUCT_FEATURES` in
 * `supabase/functions/get-agency-commercial-summary/index.ts`; the two
 * lists are pinned to each other by test.
 */
export const PRODUCT_FEATURE_IDS = [
  'automated_publishing',
  'report_export',
  'creative_intelligence',
  'paid_media',
  'paid_media_intelligence',
  'influencer_marketing',
] as const;

export type ProductFeatureId = (typeof PRODUCT_FEATURE_IDS)[number];

/**
 * How a CATALOGUE plan states a capability.
 *
 * `by_agreement` exists because `custom_v1` ships with ZERO
 * `plan_features` rows by design (`20260518120000:120-124`). Showing it
 * as all-disabled would claim the plan grants nothing; showing it as
 * unlimited would claim it grants everything. Both are false, so the
 * contract carries a third value and the page renders «حسب الاتفاق».
 */
export type PlanFeatureAvailability = 'included' | 'not_included' | 'by_agreement';

/**
 * How a CATALOGUE plan states a numeric cap.
 *
 * Kept as a discriminated union because `null` ALREADY means unlimited
 * in the caller's own `limits` block above, so `number | null` has no
 * room left to express "not predetermined".
 */
export type PlanLimitWire =
  | { readonly kind: 'limited'; readonly value: number }
  | { readonly kind: 'unlimited' }
  | { readonly kind: 'by_agreement' };

export interface PlanCatalogueEntry {
  readonly key: string;
  readonly name_en: string;
  readonly name_ar: string;
  /**
   * `true` iff this is the caller's current plan. The grandfathered
   * production tenant runs on the now-non-public `agency_v1_default`, so
   * NO entry is current for it and the page says so rather than marking
   * an arbitrary row.
   */
  readonly is_current: boolean;
  readonly limits: {
    readonly max_clients: PlanLimitWire;
    readonly max_seats: PlanLimitWire;
  };
  readonly features: ReadonlyArray<{
    readonly id: ProductFeatureId;
    readonly availability: PlanFeatureAvailability;
  }>;
}

// =====================================================================
// Task 303-A — the agency⇄client contract (externally executed PDF).
//
// SEPARATE from the agency's own Super Agency subscription above, from
// commercial standing, from agency package definitions and from
// Operating Setup. Task file: `tasks/303A-agency-client-contract-v1.md`.
//
// NO STATE HERE IMPLIES AN IN-APP SIGNATURE (§2.4). `released` means the
// document the two parties executed OUTSIDE this product is the current
// one. There is no `sent` and no `signed`.
// =====================================================================

export type ContractVersionStatus = 'draft' | 'released' | 'superseded';

export type ContractBillingInterval = 'one_time' | 'monthly' | 'quarterly' | 'yearly' | 'custom';

export type ContractPackageSource = 'agency_package' | 'manual';

export type ContractEventType =
  | 'contract_created'
  | 'version_created'
  | 'document_attached'
  | 'version_released'
  | 'version_superseded'
  | 'contract_archived'
  | 'contract_restored'
  // Task 303-B. A client recorded that it reviewed the released document.
  // It is an event ABOUT the artifact, never a state of it — the version
  // stays `released`, and there is no `signed` anything.
  | 'version_acknowledged';

/** One line of the frozen snapshot. The four keys are an allow-list. */
export interface ContractServiceLine {
  readonly name: string;
  readonly quantity?: number;
  readonly unit?: string;
  readonly notes?: string;
}

/**
 * The fields BOTH viewers receive. Everything here is client-safe, and
 * `ClientContractVersion` adds nothing to it — the agency-only fields
 * live on `AgencyContractVersion` instead, so a component that renders
 * this base type cannot accidentally read one (§2.10).
 */
export interface ContractVersionBase {
  readonly id: string;
  readonly version_number: number;
  readonly status: ContractVersionStatus;
  readonly effective_from: string;
  readonly effective_to: string | null;
  readonly term_months: number | null;
  readonly package_name: string;
  readonly amount: number;
  readonly currency: string;
  readonly billing_interval: ContractBillingInterval;
  readonly services: ReadonlyArray<ContractServiceLine>;
  readonly released_at: string | null;
  readonly superseded_at: string | null;
}

/** What a client may learn about the stored file: its name. Nothing else. */
export interface ClientContractDocument {
  readonly id: string;
  readonly filename: string;
}

/**
 * Task 303-B. The CALLER'S OWN receipt, or null.
 *
 * Two fields, both facts the client itself created. There is no digest, no
 * byte size, no storage key, no bucket, no tenant id, no client id and no
 * user id — not even the caller's own. Another user's receipt is not
 * representable here at all: the SQL that builds it is pinned to
 * `auth.uid()`, so a second `client_admin` of the same client reads null
 * rather than a colleague's name and timestamp.
 */
export interface ClientContractAcknowledgement {
  readonly acknowledged_at: string;
  readonly ack_text_version: string;
}

export interface ClientContractVersion extends ContractVersionBase {
  readonly document: ClientContractDocument | null;
  /** Task 303-B. Null until this caller acknowledges this version's current document. */
  readonly acknowledgement: ClientContractAcknowledgement | null;
  /**
   * Task 303-B. STATE, not authority: this is the released version, it
   * carries a complete document, and this caller has not acknowledged it
   * yet. Authority is `ClientContract.can_acknowledge`, which is per
   * contract — the only level it has. The endpoint re-decides both
   * regardless of what either one said.
   */
  readonly acknowledgeable: boolean;
}

export interface ClientContract {
  readonly id: string;
  readonly title: string;
  /**
   * Task 303-A-B. The CLIENT'S OWN display name — not the tenant's, and not
   * an id. It exists because `/app/contracts` answers for every client the
   * caller is assigned to, so §2.10's "the client already knows which
   * client it is" stops being true. See the migration's §5 header.
   */
  readonly client_name: string;
  readonly archived: boolean;
  /**
   * Task 303-B. PER CONTRACT, which is the ONLY level this authority has:
   * it is decided per client, and one payload can span several. There is
   * deliberately no payload-level equivalent — a summary that folded these
   * together could not gate a single contract, and one that nothing
   * rendered would be dead weight on the wire.
   */
  readonly can_acknowledge: boolean;
  readonly versions: ReadonlyArray<ClientContractVersion>;
}

export interface ClientContractOverviewData {
  readonly viewer: 'client';
  readonly can_download: boolean;
  /**
   * Task 303-B. About the READER'S OWN profile, which is why it belongs at
   * the payload level while `can_acknowledge` does not: it is true when the
   * caller carries no name and no email, and the server therefore refuses
   * to write a receipt that would name nobody.
   *
   * It is the one refusal reason the reader can act on, so the screen shows
   * "complete your profile" instead of a bare "unavailable". It discloses
   * nothing — a boolean about oneself, never a phone, an email or an id.
   */
  readonly actor_profile_incomplete: boolean;
  readonly contracts: ReadonlyArray<ClientContract>;
}

/**
 * The agency projection adds integrity evidence (`sha256`, `byte_size`)
 * and package provenance. None of it appears in the client shapes above,
 * and the two come from two different SQL functions.
 */
export interface AgencyContractDocument {
  readonly id: string;
  readonly filename: string;
  readonly content_type: string;
  readonly byte_size: number | null;
  readonly sha256: string | null;
  readonly completed_at: string | null;
}

/**
 * Task 303-B. The agency-side receipt: who acknowledged, when, and which
 * statement version they confirmed.
 *
 * The digest is deliberately NOT here. The agency already has it on
 * `AgencyContractDocument.sha256`, and the `document_attached` event
 * recorded it at upload time; repeating it in a third place would add no
 * fact and one more thing to keep in step.
 */
export interface AgencyContractAcknowledgement {
  readonly acknowledged_at: string;
  readonly ack_text_version: string;
  readonly acknowledged_by_name: string | null;
}

export interface AgencyContractVersion extends ContractVersionBase {
  readonly package_source: ContractPackageSource;
  readonly package_assignment_id: string | null;
  readonly source_package_id: string | null;
  readonly created_at: string;
  readonly has_pending_upload: boolean;
  readonly has_expired_upload: boolean;
  readonly document: AgencyContractDocument | null;
  /** Task 303-B. Empty until a client acknowledges. Newest first. */
  readonly acknowledgements: ReadonlyArray<AgencyContractAcknowledgement>;
}

export interface AgencyContractEvent {
  readonly event_type: ContractEventType;
  readonly occurred_at: string;
  readonly reason: string | null;
  readonly version_number: number | null;
  readonly actor_name: string | null;
}

export interface AgencyContract {
  readonly id: string;
  readonly title: string;
  readonly archived: boolean;
  readonly archived_at: string | null;
  readonly created_at: string;
  readonly current_version_id: string | null;
  readonly versions: ReadonlyArray<AgencyContractVersion>;
  readonly events: ReadonlyArray<AgencyContractEvent>;
}

export interface AgencyContractOverviewData {
  readonly viewer: 'agency';
  readonly client_archived: boolean;
  readonly can_manage: boolean;
  readonly can_download: boolean;
  readonly contracts: ReadonlyArray<AgencyContract>;
}

/**
 * The server decides which surface the caller gets. The page switches on
 * `viewer` and never infers authority from role strings it holds locally.
 */
export type ClientContractsOverviewData = AgencyContractOverviewData | ClientContractOverviewData;

export interface CreateContractSnapshotInput {
  readonly package_source: ContractPackageSource;
  readonly package_assignment_id?: string | null;
  readonly source_package_id?: string | null;
  readonly package_name: string;
  readonly services: ReadonlyArray<ContractServiceLine>;
  readonly amount: number;
  readonly currency: string;
  readonly billing_interval: ContractBillingInterval;
  readonly effective_from: string;
  readonly effective_to?: string | null;
  readonly term_months?: number | null;
}

/** With `contract_id` → a replacement version. Without → a new record. */
export interface CreateAgencyClientContractInput extends CreateContractSnapshotInput {
  readonly client_id?: string;
  readonly contract_id?: string;
  readonly title?: string;
}

export interface CreateAgencyClientContractData {
  readonly contract_id: string;
  readonly version_id: string;
  readonly version_number: number;
}

export interface CreateContractUploadInput {
  readonly version_id: string;
  readonly filename: string;
  readonly byte_size: number;
  readonly content_type: 'application/pdf';
}

/**
 * The upload ticket. `storage_key` and the bucket are deliberately absent
 * — the browser gets a URL bound to one object and nothing that would let
 * it aim a second PUT elsewhere (§3.2).
 */
export interface CreateContractUploadData {
  readonly document_id: string;
  readonly upload_url: string;
  readonly upload_url_expires_at: string;
  readonly required_headers: Readonly<Record<string, string>>;
}

/**
 * Completion takes ONE field. There is no checksum parameter by design:
 * the server fetches the bytes and computes the digest itself (§3.3).
 */
export interface CompleteContractUploadInput {
  readonly document_id: string;
}

export interface CompleteContractUploadData {
  readonly document_id: string;
  readonly version_id: string;
  readonly contract_id: string;
  readonly replayed: boolean;
}

export interface ReleaseContractVersionData {
  readonly contract_id: string;
  readonly version_id: string;
  readonly version_number: number;
  readonly superseded_version_id: string | null;
}

export interface SetContractArchiveStateInput {
  readonly contract_id: string;
  readonly archived: boolean;
  readonly reason?: string | null;
}

export interface SetContractArchiveStateData {
  readonly contract_id: string;
  readonly archived_at: string | null;
  readonly changed: boolean;
}

/**
 * Task 303-B — the acknowledgement request.
 *
 * TWO FIELDS, AND NEITHER IS EVIDENCE. `document_id` is re-resolved and
 * re-authorized server-side; `ack_text_version` is COMPARED to a server
 * constant and rejected on mismatch, never written from here. A
 * client-supplied version that was trusted would let a stored record claim
 * a text the user never saw.
 *
 * There is no `acknowledged_by`, no timestamp and no digest, because none
 * of those may come from a caller.
 */
export interface AcknowledgeContractVersionInput {
  readonly document_id: string;
  readonly ack_text_version: string;
}

/**
 * The receipt, as it comes back to the client.
 *
 * `replayed` is true when this submission matched one already on file: the
 * response is the ORIGINAL receipt, with its original timestamp, and
 * nothing was written. The UI says "already recorded", not "recorded".
 */
export interface AcknowledgeContractVersionData {
  readonly contract_id: string;
  readonly version_id: string;
  readonly document_id: string;
  readonly version_number: number;
  readonly filename: string;
  readonly acknowledged_at: string;
  readonly ack_text_version: string;
  readonly replayed: boolean;
}

/**
 * The download result.
 *
 * There is no `download_url` and no `expires_at` by design: the endpoint
 * streams the PDF rather than handing over a signed URL whose path is the
 * storage key (§2.10). `filename` comes back from `Content-Disposition`
 * and may be null if the header is missing, in which case the caller names
 * the file rather than inventing one.
 */
export interface ContractDocumentDownload {
  readonly blob: Blob;
  readonly filename: string | null;
}

// ─────────────────────────────────────────────────────────────────────
// Task 306A — the operator's per-organization detail read
//
// Appended as a single contiguous block per the shared-file protocol in
// `tasks/295A-commercial-foundation-and-staging.md` §9.3. Never
// interleave another lane's additions into this block.
//
// PLATFORM OPERATOR ONLY. `get-organization-commercial-detail` refuses
// every tenant actor as its first statement after authentication, and
// every table it reads is additionally gated by an
// `app.is_system_admin()` RLS policy — so this shape can only ever be
// materialised for an operator (306A §1.3).
//
// NOTHING HERE IS CLIENT-SCOPED. No client, content, assignment, seat or
// tenant-user identity appears in any of these types, and no auth uuid
// does either: `activated_by_user_id` and the ledger's `actor_user_id`
// are deliberately absent (306A §1.3).
// ─────────────────────────────────────────────────────────────────────

/** The eight Task 299 legal/contact fields, in operator reading order. */
export const AGENCY_PROFILE_FIELD_KEYS = [
  'legal_name',
  'country_code',
  'city',
  'phone_e164',
  'commercial_registration_no',
  'vat_no',
  'billing_contact_name',
  'billing_contact_email',
] as const;

export type AgencyProfileFieldKey = (typeof AGENCY_PROFILE_FIELD_KEYS)[number];

/**
 * The two fields Task 299 froze as OPTIONAL, and the six required ones
 * derived from them by set difference.
 *
 * ONE authoritative required subset for the whole web app: the agency's
 * own onboarding form (`AgencyOnboardingPage`) and the operator's detail
 * panel read this same constant, and the server derives the identical set
 * the same way in `get-organization-commercial-detail`. All three are
 * pinned by test against the SQL that actually decides completion —
 * `app.set_organization_profile_completed_at()` in
 * `supabase/migrations/20260908120000_agency_profile.sql`, which writes
 * `completed_at` when exactly these six are non-blank and never looks at
 * commercial registration or VAT.
 *
 * Displaying all eight and REQUIRING six are different questions.
 * `AGENCY_PROFILE_FIELD_KEYS` answers the first; this answers the second.
 */
export const OPTIONAL_AGENCY_PROFILE_FIELD_KEYS = ['commercial_registration_no', 'vat_no'] as const;

export type OptionalAgencyProfileFieldKey = (typeof OPTIONAL_AGENCY_PROFILE_FIELD_KEYS)[number];

export type RequiredAgencyProfileFieldKey = Exclude<
  AgencyProfileFieldKey,
  OptionalAgencyProfileFieldKey
>;

export const REQUIRED_AGENCY_PROFILE_FIELD_KEYS: ReadonlyArray<RequiredAgencyProfileFieldKey> =
  AGENCY_PROFILE_FIELD_KEYS.filter(
    (key): key is RequiredAgencyProfileFieldKey =>
      !(OPTIONAL_AGENCY_PROFILE_FIELD_KEYS as ReadonlyArray<string>).includes(key),
  );

export interface CommercialDetailOrganization {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly created_at: string;
}

export type CommercialDetailProfile = Readonly<Record<AgencyProfileFieldKey, string | null>> & {
  readonly completed_at: string | null;
};

/**
 * Completion is DERIVED SERVER-SIDE over
 * {@link REQUIRED_AGENCY_PROFILE_FIELD_KEYS} — the SIX required fields,
 * not the eight returned — and reported separately from
 * `profile.completed_at`, which is the server's own verdict written by a
 * trigger. Two independent statements of one fact is the point: a
 * `completed_at` standing next to a missing required field is something
 * an operator must be able to see. Both statements now score the same
 * six, so a disagreement between them is a real defect rather than the
 * guaranteed artefact it was when this counted all eight.
 *
 * `total` is therefore 6, not the number of fields in `profile`. The
 * panel says so.
 */
export interface CommercialDetailProfileCompletion {
  readonly filled: number;
  readonly total: number;
  readonly missing: ReadonlyArray<RequiredAgencyProfileFieldKey>;
}

export interface CommercialDetailStanding {
  readonly commercial_state: CommercialState;
  readonly payment_status: string;
  readonly activated_at: string | null;
  readonly suspended_at: string | null;
  readonly terminated_at: string | null;
  /** Operator-authored. Never rendered to a tenant actor anywhere. */
  readonly notes: string | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
}

export interface CommercialDetailSubscription {
  readonly plan_key: string | null;
  readonly plan_name_en: string | null;
  readonly plan_name_ar: string | null;
  readonly state: string;
  readonly started_at: string;
  readonly current_period_ends_at: string | null;
}

/**
 * One row of the append-only transition ledger
 * (`organization_commercial_state_events`). `reason` is required on every
 * transition by 295A invariant A4, and it is the reason this projection
 * exists at all: an audit record nobody can read is not auditable.
 */
export interface CommercialDetailEvent {
  readonly action: string;
  readonly from_state: string | null;
  readonly to_state: string;
  readonly from_payment_status: string | null;
  readonly to_payment_status: string;
  readonly reason: string;
  readonly created_at: string;
}

/**
 * One assignable catalogue plan — Task 307A.
 *
 * `is_active AND is_public`, which after 20260909120000 (D3 / CLP-1.6) is
 * exactly {starter_v1, growth_v1, custom_v1}. Named by KEY and never by
 * id: the key is what `set-organization-subscription-plan` accepts, so no
 * plan uuid has to travel in either direction.
 */
export interface AssignablePlan {
  readonly key: string;
  readonly name_ar: string | null;
  readonly name_en: string | null;
}

/**
 * One row of the append-only manual plan-assignment ledger — Task 307A.
 * `from_plan_key` is null for the first assignment on an organization
 * that had no current subscription.
 */
export interface CommercialDetailPlanEvent {
  readonly from_plan_key: string | null;
  readonly to_plan_key: string;
  readonly reason: string;
  readonly created_at: string;
}

export interface OrganizationCommercialDetailData {
  readonly organization: CommercialDetailOrganization;
  readonly profile: CommercialDetailProfile | null;
  readonly profile_completion: CommercialDetailProfileCompletion;
  readonly commercial: CommercialDetailStanding;
  readonly subscription: CommercialDetailSubscription | null;
  readonly history: ReadonlyArray<CommercialDetailEvent>;
  readonly history_truncated: boolean;
  readonly assignable_plans: ReadonlyArray<AssignablePlan>;
  readonly plan_history: ReadonlyArray<CommercialDetailPlanEvent>;
  readonly plan_history_truncated: boolean;
}

/**
 * `set-organization-subscription-plan` — Task 307A.
 *
 * A NARROW projection of `public.subscriptions`. The RPC returns the row
 * whole; `provider_customer_ref`, `provider_subscription_ref` and the
 * free-form `metadata` are none of the operator's business and never
 * reach the wire.
 */
export interface SubscriptionPlanAssignment {
  readonly subscription_id: string;
  readonly organization_id: string;
  readonly plan_key: string;
  readonly plan_name_ar: string | null;
  readonly plan_name_en: string | null;
  readonly state: string;
  readonly started_at: string;
  readonly current_period_ends_at: string | null;
}

export interface SetOrganizationSubscriptionPlanData {
  readonly subscription: SubscriptionPlanAssignment;
}
